import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import { BookOpen, User, Bell, Calendar, Edit2, Save, Settings, Building, CheckCircle, LayoutDashboard, Users, MessageSquare, Layers, Clock, ArrowLeft, ArrowRight, FileText, Award, Mail, Plus, Trash2, ArrowUpDown, Sparkles, CheckSquare, HelpCircle, X, AlertCircle, Info, GraduationCap, Shield, Eye, FileSpreadsheet, Search, Download, Upload, MoreVertical, Maximize2, Minimize2, Play, Pause, ChevronLeft, ChevronRight, CheckCircle2, RotateCcw, AlertTriangle, ShieldCheck, ZoomIn, Brain } from 'lucide-react';
import UniAiReports from '../components/university/UniAiReports';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  Cell,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';



import CardInfoTooltip from '../components/CardInfoTooltip';
import Loader from '../components/Loader';
import FacultyStudentDirectory from '../components/faculty/FacultyStudentDirectory';
import FacultySkillGapAnalysis from '../components/faculty/FacultySkillGapAnalysis';
import AttendanceExportControls from '../components/AttendanceExportControls';
import DiscussionsPage from './DiscussionsPage';
import ChatPage from './ChatPage';
import { FeatureGate, SubFeatureGate, useFeatures } from '../components/FeatureGate';
import FeatureUnavailable from '../components/FeatureUnavailable';
import AiDisclaimerCard from '../components/AiDisclaimerCard';
import '../App.css';
import './UniDashboard.css'; // Add exact styling
import './FacultyDashboard.css';

import CalendarLib from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

// Lightweight inline markdown renderer — no external dependency needed
const renderMarkdown = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // H3 ###
    if (line.startsWith('### ')) {
      elements.push(<h3 key={key++} style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0f172a', margin: '20px 0 6px', borderBottom: '2px solid #e2e8f0', paddingBottom: '6px' }}>{line.slice(4)}</h3>);
    }
    // H4 ####
    else if (line.startsWith('#### ')) {
      elements.push(<h4 key={key++} style={{ fontSize: '0.95rem', fontWeight: '700', color: '#334155', margin: '14px 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{line.slice(5)}</h4>);
    }
    // H2 ##
    else if (line.startsWith('## ')) {
      elements.push(<h2 key={key++} style={{ fontSize: '1.25rem', fontWeight: '800', color: '#0f172a', margin: '22px 0 8px' }}>{line.slice(3)}</h2>);
    }
    // H1 #
    else if (line.startsWith('# ')) {
      elements.push(<h1 key={key++} style={{ fontSize: '1.5rem', fontWeight: '900', color: '#0f172a', margin: '24px 0 10px' }}>{line.slice(2)}</h1>);
    }
    // HR ---
    else if (/^[-*_]{3,}$/.test(line.trim())) {
      elements.push(<hr key={key++} style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '16px 0' }} />);
    }
    // Bullet * or -
    else if (/^[*\-] /.test(line)) {
      const content = inlineFormat(line.slice(2));
      elements.push(
        <div key={key++} style={{ display: 'flex', gap: '8px', margin: '4px 0', alignItems: 'flex-start' }}>
          <span style={{ color: '#6366f1', fontWeight: '700', marginTop: '2px', flexShrink: 0 }}>•</span>
          <span style={{ color: '#334155', lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: content }} />
        </div>
      );
    }
    // Empty line
    else if (line.trim() === '') {
      elements.push(<div key={key++} style={{ height: '8px' }} />);
    }
    // Regular paragraph
    else {
      const content = inlineFormat(line);
      elements.push(<p key={key++} style={{ color: '#334155', lineHeight: '1.7', margin: '4px 0' }} dangerouslySetInnerHTML={{ __html: content }} />);
    }
  }
  return elements;
};

const escapeHtml = (str) => {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

// Inline formatting: **bold**, *italic*, `code`
const inlineFormat = (text) => {
  const safeText = escapeHtml(text);
  return safeText
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:#0f172a;font-weight:700">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code style="background:#f1f5f9;padding:1px 5px;border-radius:4px;font-size:0.85em;color:#7c3aed">$1</code>');
};

const FacultyDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [assignedCourses, setAssignedCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [facultyStudents, setFacultyStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [courseDetailSection, setCourseDetailSection] = useState('attendance');

  const [attendanceDate, setAttendanceDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [attendanceTime, setAttendanceTime] = useState(() => new Date().toTimeString().slice(0, 5));
  const [attendanceSessions, setAttendanceSessions] = useState({});
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [attendanceDraftRecords, setAttendanceDraftRecords] = useState({});
  const [attendanceUsnSort, setAttendanceUsnSort] = useState('asc');
  const [attendanceSaveStatus, setAttendanceSaveStatus] = useState('');
  const [attendanceUploadStatus, setAttendanceUploadStatus] = useState('');
  const [testMarksList, setTestMarksList] = useState({});
  const [testMarksRecords, setTestMarksRecords] = useState({});
  const [testMarksDraftRecords, setTestMarksDraftRecords] = useState({});
  const [testMarksSaveStatus, setTestMarksSaveStatus] = useState('');
  const [testMarksUploadStatus, setTestMarksUploadStatus] = useState('');
  const [maximizedLmsSection, setMaximizedLmsSection] = useState(null);
  const [studentTableSearch, setStudentTableSearch] = useState('');
  const [openLmsMenu, setOpenLmsMenu] = useState(null);
  const [fullTestReports, setFullTestReports] = useState({});
  const [newTestName, setNewTestName] = useState('');
  const [newMaxMarks, setNewMaxMarks] = useState(100);
  const [testBuilderType, setTestBuilderType] = useState(() => {
    return localStorage.getItem('saved_test_builder_type') || 'mcq';
  });
  const [testBuilderCount, setTestBuilderCount] = useState(20);
  const [testBuilderItems, setTestBuilderItems] = useState(() => {
    try {
      const saved = localStorage.getItem('saved_test_builder_items');
      if (saved) return JSON.parse(saved);
      const latest = localStorage.getItem('latest_faculty_assigned_test');
      if (latest) {
        const parsed = JSON.parse(latest);
        if (parsed?.questions && Array.isArray(parsed.questions)) return parsed.questions;
      }
    } catch { }
    return [];
  });
  const [testBuilderLoading, setTestBuilderLoading] = useState(false);
  const [selectedReportStudent, setSelectedReportStudent] = useState(null);
  const [testBuilderTitle, setTestBuilderTitle] = useState(() => {
    return localStorage.getItem('saved_test_builder_title') || 'Midterm Assessment - AI & Engineering';
  });

  // AI Disclaimer gate
  const [showAiDisclaimer, setShowAiDisclaimer] = useState(false);
  const [aiDisclaimerFeature, setAiDisclaimerFeature] = useState('');
  const [pendingAiAction, setPendingAiAction] = useState(null);

  const requireAiDisclaimer = (featureName, action) => {
    if (sessionStorage.getItem('ai_disclaimer_agreed')) {
      action();
    } else {
      setAiDisclaimerFeature(featureName);
      setPendingAiAction(() => action);
      setShowAiDisclaimer(true);
    }
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [fullDescCourse, setFullDescCourse] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [openMaterials, setOpenMaterials] = useState({});
  const [isEditingFinalAssessment, setIsEditingFinalAssessment] = useState(false);
  const [finalAssessmentDraft, setFinalAssessmentDraft] = useState([]);
  const [finalAssessmentSaveState, setFinalAssessmentSaveState] = useState('');
  const [popNotification, setPopNotification] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Notifications & Calendar State
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Assessment Proctoring Reports State (Phase 3 Audit & Playback)
  const [proctoringReports, setProctoringReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsSearch, setReportsSearch] = useState('');
  const [reportsCategoryFilter, setReportsCategoryFilter] = useState('ALL');
  const [selectedProctorReport, setSelectedProctorReport] = useState(null);
  const [activeSnapshotIndex, setActiveSnapshotIndex] = useState(0);
  const [isPlayingSequence, setIsPlayingSequence] = useState(false);
  const [facultyAuditNotes, setFacultyAuditNotes] = useState('');
  const [facultyVerdict, setFacultyVerdict] = useState('VERIFIED_DISQUALIFIED');
  const [isSubmittingVerdict, setIsSubmittingVerdict] = useState(false);
  const [lightboxSnapshot, setLightboxSnapshot] = useState(null);

  // Auto-play incident snapshots sequence
  useEffect(() => {
    let playInterval = null;
    if (isPlayingSequence && selectedProctorReport) {
      const events = selectedProctorReport.violations_data || [];
      if (events.length > 1) {
        playInterval = setInterval(() => {
          setActiveSnapshotIndex(prev => (prev + 1) % events.length);
        }, 2500);
      } else {
        setIsPlayingSequence(false);
      }
    }
    return () => {
      if (playInterval) clearInterval(playInterval);
    };
  }, [isPlayingSequence, selectedProctorReport]);

  // Keyboard navigation for proctoring snapshots modal (Arrow keys, Space to play, Esc to close)
  useEffect(() => {
    if (!selectedProctorReport) return;
    const handleKeyDown = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(e.target?.tagName)) return;
      const events = Array.isArray(selectedProctorReport.violations_data) ? selectedProctorReport.violations_data : [];
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveSnapshotIndex(prev => Math.max(0, prev - 1));
        setIsPlayingSequence(false);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveSnapshotIndex(prev => Math.min(events.length - 1, prev + 1));
        setIsPlayingSequence(false);
      } else if (e.key === ' ') {
        e.preventDefault();
        setIsPlayingSequence(prev => !prev);
      } else if (e.key === 'Escape') {
        if (lightboxSnapshot) {
          setLightboxSnapshot(null);
        } else {
          setSelectedProctorReport(null);
          setIsPlayingSequence(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedProctorReport, lightboxSnapshot]);

  const formatViolationType = (type) => {
    if (!type) return 'Incident Event';
    const map = {
      'MULTIPLE_PERSONS_DETECTED': 'Multiple Faces',
      'NO_PERSON_DETECTED': 'Face Absent',
      'HEAD_MOVEMENT_AWAY': 'Looking Away',
      'PHONE_DETECTED': 'Phone Detected',
      'TAB_SWITCH': 'Tab Switch',
      'FULLSCREEN_EXIT': 'Exited Fullscreen',
      'SCREEN_SHARE_STOPPED': 'Screen Share Stopped',
      'MULTI_MONITOR_DETECTED': 'Multi-Monitor',
      'CLIPBOARD_BLOCKED': 'Copy / Paste Attempt',
      'VISUAL_ANOMALY': 'Visual Anomaly',
      'SCREEN_ANOMALY': 'Screen Anomaly',
      'PROCTORING_FLAG': 'Proctoring Alert'
    };
    return map[type] || type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  };

  const getViolationBadgeStyle = (type) => {
    switch (type) {
      case 'PHONE_DETECTED':
      case 'MULTIPLE_PERSONS_DETECTED':
        return { bg: '#fee2e2', text: '#dc2626', border: '#fca5a5' };
      case 'TAB_SWITCH':
      case 'SCREEN_SHARE_STOPPED':
      case 'FULLSCREEN_EXIT':
        return { bg: '#fef3c7', text: '#b45309', border: '#fcd34d' };
      case 'HEAD_MOVEMENT_AWAY':
      case 'NO_PERSON_DETECTED':
        return { bg: '#e0e7ff', text: '#4338ca', border: '#a5b4fc' };
      default:
        return { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' };
    }
  };

  const isSkillAssessmentReport = (report) => {
    if (!report) return true;
    if (report.is_skill_assessment !== undefined) return report.is_skill_assessment;
    const title = (report.assessment_title || '').toLowerCase();
    return !['interviewer', 'counsellor', 'counselor', 'tutor', 'scorer'].some(k => title.includes(k));
  };

  const handleOpenProctorAudit = (report) => {
    setSelectedProctorReport(report);
    setActiveSnapshotIndex(0);
    setIsPlayingSequence(false);
    const isSkill = isSkillAssessmentReport(report);
    if (isSkill) {
      setFacultyVerdict(report.status === 'RETAKE_GRANTED' ? 'RETAKE_GRANTED' : 'VERIFIED_DISQUALIFIED');
    } else {
      setFacultyVerdict(report.status === 'FLAGGED' || report.status === 'VERIFIED_DISQUALIFIED' ? 'FLAGGED' : 'OVERRULED_CLEAN');
    }
    setFacultyAuditNotes(report.faculty_audit?.notes || '');
  };

  const handleVerifyVerdict = async () => {
    if (!selectedProctorReport) return;
    setIsSubmittingVerdict(true);
    try {
      const res = await api.post(`/proctoring/reports/${selectedProctorReport.id}/verify`, {
        status: facultyVerdict,
        faculty_notes: facultyAuditNotes
      });
      triggerNotification('success', 'Verdict Updated', `Report #${selectedProctorReport.id} successfully updated to ${facultyVerdict}`);
      setSelectedProctorReport(prev => prev ? {
        ...prev,
        status: facultyVerdict,
        report_summary: res.data.report_summary,
        faculty_audit: res.data.faculty_audit
      } : null);
      fetchProctoringReports();
    } catch (err) {
      triggerNotification('error', 'Update Failed', err.response?.data?.detail || 'Failed to submit audit verdict.');
    } finally {
      setIsSubmittingVerdict(false);
    }
  };

  const fetchProctoringReports = async () => {
    setReportsLoading(true);
    try {
      const res = await api.get('/proctoring/reports');
      setProctoringReports(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching proctoring reports:", err);
      setProctoringReports([]);
    } finally {
      setReportsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'assessmentReports') {
      fetchProctoringReports();
    }
  }, [activeTab]);

  const triggerNotification = (type, title, message) => {
    setPopNotification({ type, title, message });
    setTimeout(() => {
      setPopNotification(prev => (prev && prev.title === title ? null : prev));
    }, 5000);
  };

  useEffect(() => {
    fetchProfile();
    fetchAssignedCourses();
    fetchFacultyStudents();
    fetchProctoringReports();
  }, []);


  useEffect(() => {
    if (location.state?.showSettings || location.state?.showProfile) {
      setActiveTab('profile');
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.showDiscussions) {
      setActiveTab('discussions');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem('saved_test_builder_items', JSON.stringify(testBuilderItems || []));
      localStorage.setItem('saved_test_builder_type', testBuilderType || 'mcq');
      localStorage.setItem('saved_test_builder_title', testBuilderTitle || 'Midterm Assessment');
      if (testBuilderItems && testBuilderItems.length > 0) {
        const totalMarks = testBuilderItems.reduce((acc, it) => acc + (Number(it.marks) || 0), 0);
        const payload = {
          testName: testBuilderTitle || "Faculty Assigned Ability Test",
          maxMarks: totalMarks > 0 ? totalMarks : 100,
          type: testBuilderType,
          questions: testBuilderItems,
          assignedAt: new Date().toISOString()
        };
        localStorage.setItem('latest_faculty_assigned_test', JSON.stringify(payload));
      }
    } catch { }
  }, [testBuilderItems, testBuilderTitle, testBuilderType]);

  useEffect(() => {
    if (selectedAssignment?.id) {
      fetchAttendanceForAssignment(selectedAssignment.id);
      fetchTestMarksForAssignment(selectedAssignment.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssignment?.id]);

  useEffect(() => {
    setIsEditingFinalAssessment(false);
    setFinalAssessmentDraft([]);
    setFinalAssessmentSaveState('');
  }, [selectedAssignment?.id]);

  // Load previously assigned test from DB when assignment is selected
  useEffect(() => {
    const loadSavedTest = async () => {
      if (!selectedAssignment?.id) return;
      try {
        const res = await api.get(`/faculty/lms/assigned/${selectedAssignment.id}/test`);
        if (res.data && res.data.questions && res.data.questions.length > 0) {
          setTestBuilderItems(normalizeQuizQuestions(res.data.questions));
          setTestBuilderTitle(res.data.testName || 'Midterm Assessment');
          setTestBuilderType(res.data.type || 'conceptual');
        }
      } catch { /* no saved test yet — keep current builder state */ }
    };
    loadSavedTest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssignment?.id]);

  useEffect(() => {
    setTestBuilderItems(prev => normalizeQuizQuestions(prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/auth/me');
      setProfile(res.data);
      if (res.data.faculty_profile) {
        setFormData({
          full_name: res.data.faculty_profile.full_name || '',
          phone_number: res.data.phone_number || '',
          faculty_id: res.data.faculty_profile.faculty_id || '',
          department: res.data.faculty_profile.department || '',
          designation: res.data.faculty_profile.designation || ''
        });
      }
    } catch (err) {
      console.error("Error fetching profile", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await api.get('/chat/conversations');
      const totalUnread = res.data.reduce((acc, curr) => acc + (curr.unread_count || 0), 0);
      setUnreadMessagesCount(totalUnread);
    } catch (err) {
      console.error("Failed to fetch unread messages count", err);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const hydrateAssignmentMetrics = async (courses) => {
    if (!Array.isArray(courses) || courses.length === 0) {
      setIsAnalyticsLoading(false);
      return;
    }

    setIsAnalyticsLoading(true);
    try {
      await Promise.all(
        courses.map(async (course) => {
          await Promise.all([
            fetchAttendanceForAssignment(course.id),
            fetchTestMarksForAssignment(course.id)
          ]);
        })
      );
    } finally {
      setIsAnalyticsLoading(false);
    }
  };

  const fetchAssignedCourses = async () => {
    setCoursesLoading(true);
    try {
      const res = await api.get('/faculty/lms/assigned');
      const courses = Array.isArray(res.data) ? res.data : [];
      setAssignedCourses(courses);
      await hydrateAssignmentMetrics(courses);
    } catch (err) {
      console.error("Error fetching assigned LMS courses", err);
      setAssignedCourses([]);
      setIsAnalyticsLoading(false);
    } finally {
      setCoursesLoading(false);
    }
  };

  const getLessonCount = (course) => {
    if (!Array.isArray(course?.modules_data)) return 0;
    return course.modules_data.reduce((total, module) => total + (Array.isArray(module.lessons) ? module.lessons.length : 0), 0);
  };

  const fetchFacultyStudents = async () => {
    setStudentsLoading(true);
    try {
      const res = await api.get('/profile/faculty/students');
      setFacultyStudents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching faculty students", err);
      setFacultyStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  };

  const normalizeBatchValue = (value) => String(value || '').trim().toLowerCase();

  const getStudentBatchTokens = (student) => {
    const tokens = new Set(['all', 'all batches', 'general']);
    const add = (value) => {
      const normalized = normalizeBatchValue(value);
      if (normalized) tokens.add(normalized);
    };

    add(student.academic_batch);
    add(student.graduation_year);
    if (student.graduation_year) add(`Batch of ${student.graduation_year}`);
    if (student.academic_batch) add(`Batch of ${student.academic_batch}`);
    add(student.degree);
    add(student.branch);
    add(student.department);
    add(student.graduation_level);
    add(student.program);

    const parts = [];
    if (student.graduation_level) parts.push(String(student.graduation_level).trim());
    if (student.program) parts.push(String(student.program).trim());
    const branchValue = student.branch || student.degree || student.department;
    if (branchValue) parts.push(String(branchValue).trim());
    const baseString = parts.join(' - ');
    const yearValue = student.academic_batch || student.graduation_year;

    if (baseString && yearValue) add(`${baseString} (${String(yearValue).trim()})`);
    if (baseString) add(baseString);
    if (branchValue && yearValue) {
      add(`${branchValue} - ${yearValue}`);
      if (student.graduation_level) add(`${student.graduation_level} - ${branchValue} - ${yearValue}`);
    }

    return tokens;
  };

  const getStudentsForAssignment = (assignment) => {
    if (!assignment) return [];
    const assignedBatch = normalizeBatchValue(assignment.batch || 'All Batches');
    if (!assignedBatch || assignedBatch === 'all' || assignedBatch === 'all batches') {
      return facultyStudents;
    }
    return facultyStudents.filter(student => getStudentBatchTokens(student).has(assignedBatch));
  };

  const getStudentUsnSortValue = (student) => String(student.usn || '').trim().toLowerCase();

  const sortStudentsByUsn = (students) => {
    return [...students].sort((firstStudent, secondStudent) => {
      const firstUsn = getStudentUsnSortValue(firstStudent);
      const secondUsn = getStudentUsnSortValue(secondStudent);

      if (!firstUsn && !secondUsn) {
        return String(firstStudent.full_name || '').localeCompare(String(secondStudent.full_name || ''));
      }
      if (!firstUsn) return 1;
      if (!secondUsn) return -1;

      const comparison = firstUsn.localeCompare(secondUsn, undefined, { numeric: true, sensitivity: 'base' });
      return attendanceUsnSort === 'asc' ? comparison : -comparison;
    });
  };

  const getAttendanceStudentId = (student) => student.user_id || student.student_user_id || student.id;

  const getAttendanceSessionsKey = (assignmentId) => `faculty_attendance_sessions_${assignmentId}`;
  const getAttendanceStorageKey = (assignmentId, sessionId) => `faculty_attendance_${assignmentId}_${sessionId}`;

  const getAttendanceSessionId = (date, time) => `${date}_${time || '00:00'}`;

  const getAttendanceSessionFromId = (sessionId) => {
    const [date, time = '00:00'] = String(sessionId || '').split('_');
    return { date, time };
  };

  const loadAttendanceSessions = (assignmentId) => {
    if (!assignmentId) return [];
    try {
      const sessions = JSON.parse(localStorage.getItem(getAttendanceSessionsKey(assignmentId)) || '[]');
      return Array.isArray(sessions) ? sessions : [];
    } catch {
      return [];
    }
  };

  const saveAttendanceSessions = (assignmentId, sessions) => {
    localStorage.setItem(getAttendanceSessionsKey(assignmentId), JSON.stringify(sessions));
    setAttendanceSessions(prev => ({ ...prev, [assignmentId]: sessions }));
  };

  const getAttendanceSessions = (assignmentId) => {
    return attendanceSessions[assignmentId] || loadAttendanceSessions(assignmentId);
  };

  const fetchAttendanceForAssignment = async (assignmentId) => {
    if (!assignmentId) return [];
    try {
      const res = await api.get(`/faculty/lms/assigned/${assignmentId}/attendance`);
      const records = Array.isArray(res.data) ? res.data : [];
      const sessionMap = new Map();
      const nextRecords = {};

      records.forEach(record => {
        const sessionId = getAttendanceSessionId(record.class_date, record.class_time);
        sessionMap.set(sessionId, {
          id: sessionId,
          date: record.class_date,
          time: record.class_time
        });

        const recordKey = `${assignmentId}-${sessionId}`;
        nextRecords[recordKey] = {
          ...(nextRecords[recordKey] || {}),
          [record.student_id]: record.status || 'present'
        };
      });

      const sessions = Array.from(sessionMap.values())
        .sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`));

      localStorage.setItem(getAttendanceSessionsKey(assignmentId), JSON.stringify(sessions));
      Object.entries(nextRecords).forEach(([recordKey, recordsForSession]) => {
        const sessionId = recordKey.replace(`${assignmentId}-`, '');
        localStorage.setItem(getAttendanceStorageKey(assignmentId, sessionId), JSON.stringify(recordsForSession));
      });

      setAttendanceSessions(prev => ({ ...prev, [assignmentId]: sessions }));
      setAttendanceRecords(prev => ({ ...prev, ...nextRecords }));
      return sessions;
    } catch (err) {
      console.error("Error loading attendance records", err);
      return [];
    }
  };

  const formatAttendanceDate = (date) => {
    if (!date) return 'Date';
    const parsedDate = new Date(`${date}T00:00:00`);
    return parsedDate.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatAttendanceTime = (time) => {
    if (!time || /^(am|pm)$/i.test(String(time).trim())) return '';
    const timeParts = String(time).trim().match(/^(\d{1,2}):(\d{2})(?:\s*(am|pm))?$/i);
    if (!timeParts) return '';
    let hours = Number(timeParts[1]);
    const minutes = Number(timeParts[2]);
    if (timeParts[3]) {
      hours = hours % 12 + (timeParts[3].toLowerCase() === 'pm' ? 12 : 0);
    }
    const displayHours = hours % 12 || 12;
    return `${String(displayHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const formatAttendancePeriod = (time) => {
    const normalizedTime = String(time || '').trim().toUpperCase();
    if (normalizedTime === 'AM' || normalizedTime === 'PM') return normalizedTime;
    const hours = Number(normalizedTime.split(':')[0]);
    if (!Number.isFinite(hours)) return '';
    return hours >= 12 ? 'PM' : 'AM';
  };

  const saveAttendanceToServer = async (assignmentId, records) => {
    if (!assignmentId || records.length === 0) return;
    await api.post(`/faculty/lms/assigned/${assignmentId}/attendance`, { records });
  };

  const addAttendanceSession = (assignmentId, students = []) => {
    if (!assignmentId || !attendanceDate || !attendanceTime) return;
    const sessionId = getAttendanceSessionId(attendanceDate, attendanceTime);
    const currentSessions = getAttendanceSessions(assignmentId);
    if (currentSessions.some(session => session.id === sessionId)) return;

    const nextSessions = [
      ...currentSessions,
      {
        id: sessionId,
        date: attendanceDate,
        time: attendanceTime
      }
    ].sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`));

    saveAttendanceSessions(assignmentId, nextSessions);

    const defaultRecords = students.map(student => [getAttendanceStudentId(student), 'present']);
    const nextRecords = Object.fromEntries(defaultRecords);
    localStorage.setItem(getAttendanceStorageKey(assignmentId, sessionId), JSON.stringify(nextRecords));
    setAttendanceRecords(prev => ({ ...prev, [`${assignmentId}-${sessionId}`]: nextRecords }));
    setAttendanceSaveStatus('');
  };

  const removeAttendanceSession = async (assignmentId, sessionId) => {
    const nextSessions = getAttendanceSessions(assignmentId).filter(session => session.id !== sessionId);
    const session = getAttendanceSessionFromId(sessionId);
    localStorage.removeItem(getAttendanceStorageKey(assignmentId, sessionId));
    saveAttendanceSessions(assignmentId, nextSessions);
    setAttendanceRecords(prev => {
      const nextRecords = { ...prev };
      delete nextRecords[`${assignmentId}-${sessionId}`];
      return nextRecords;
    });
    setAttendanceDraftRecords(prev => {
      const nextRecords = { ...prev };
      delete nextRecords[`${assignmentId}-${sessionId}`];
      return nextRecords;
    });
    try {
      await api.delete(`/faculty/lms/assigned/${assignmentId}/attendance`, {
        params: {
          class_date: session.date,
          class_time: session.time
        }
      });
    } catch (err) {
      console.error("Error deleting attendance session", err);
    }
  };

  const loadAttendanceForSession = (assignmentId, sessionId) => {
    if (!assignmentId || !sessionId) return {};
    try {
      return JSON.parse(localStorage.getItem(getAttendanceStorageKey(assignmentId, sessionId)) || '{}');
    } catch {
      return {};
    }
  };

  const getAttendanceStatus = (assignmentId, sessionId, studentId) => {
    const recordKey = `${assignmentId}-${sessionId}`;
    const draftRecords = attendanceDraftRecords[recordKey];
    if (draftRecords && Object.prototype.hasOwnProperty.call(draftRecords, studentId)) {
      return draftRecords[studentId];
    }
    const dateRecords = attendanceRecords[recordKey] || loadAttendanceForSession(assignmentId, sessionId);
    return dateRecords[studentId] || 'present';
  };

  const updateAttendanceStatus = (assignmentId, sessionId, studentId, status) => {
    const recordKey = `${assignmentId}-${sessionId}`;
    const currentRecords = attendanceDraftRecords[recordKey] || attendanceRecords[recordKey] || loadAttendanceForSession(assignmentId, sessionId);
    const nextRecords = { ...currentRecords, [studentId]: status };
    setAttendanceDraftRecords(prev => ({ ...prev, [recordKey]: nextRecords }));
    setAttendanceSaveStatus('unsaved');
  };

  const saveVisibleAttendance = async (assignmentId, students, sessions) => {
    if (!assignmentId || sessions.length === 0 || students.length === 0) return;
    const records = sessions.flatMap(session => (
      students.map(student => {
        const studentId = getAttendanceStudentId(student);
        return {
          student_id: studentId,
          class_date: session.date,
          class_time: session.time,
          status: getAttendanceStatus(assignmentId, session.id, studentId)
        };
      })
    ));

    setAttendanceSaveStatus('saving');
    try {
      await saveAttendanceToServer(assignmentId, records);
      const savedRecordMap = {};
      sessions.forEach(session => {
        const recordKey = `${assignmentId}-${session.id}`;
        const sessionRecords = {};
        students.forEach(student => {
          const studentId = getAttendanceStudentId(student);
          sessionRecords[studentId] = getAttendanceStatus(assignmentId, session.id, studentId);
        });
        savedRecordMap[recordKey] = sessionRecords;
        localStorage.setItem(getAttendanceStorageKey(assignmentId, session.id), JSON.stringify(sessionRecords));
      });
      localStorage.setItem(getAttendanceSessionsKey(assignmentId), JSON.stringify(sessions));
      setAttendanceRecords(prev => ({ ...prev, ...savedRecordMap }));
      setAttendanceDraftRecords(prev => {
        const nextDraftRecords = { ...prev };
        sessions.forEach(session => {
          delete nextDraftRecords[`${assignmentId}-${session.id}`];
        });
        return nextDraftRecords;
      });
      setAttendanceSaveStatus('saved');
    } catch (err) {
      console.error("Error saving attendance", err);
      setAttendanceSaveStatus('error');
    }
  };

  const getTestMarksListKey = (assignmentId) => `faculty_test_marks_list_${assignmentId}`;
  const getTestMarksStorageKey = (assignmentId, testName) => `faculty_test_marks_${assignmentId}_${testName}`;

  const loadTestMarksList = (assignmentId) => {
    if (!assignmentId) return [];
    try {
      const list = JSON.parse(localStorage.getItem(getTestMarksListKey(assignmentId)) || '[]');
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  };

  const saveTestMarksList = (assignmentId, list) => {
    localStorage.setItem(getTestMarksListKey(assignmentId), JSON.stringify(list));
    setTestMarksList(prev => ({ ...prev, [assignmentId]: list }));
  };

  const getTestMarksListData = (assignmentId) => {
    return testMarksList[assignmentId] || loadTestMarksList(assignmentId);
  };

  const loadTestMarksForSession = (assignmentId, testName) => {
    if (!assignmentId || !testName) return {};
    try {
      return JSON.parse(localStorage.getItem(getTestMarksStorageKey(assignmentId, testName)) || '{}');
    } catch {
      return {};
    }
  };

  const courseAnalytics = useMemo(() => {
    return assignedCourses.map((courseItem) => {
      const courseStudents = getStudentsForAssignment(courseItem);
      const assignmentId = courseItem.id;
      const sessions = attendanceSessions[assignmentId] || [];

      const attendanceValues = sessions
        .map((session) => {
          const recordKey = `${assignmentId}-${session.id}`;
          const record = attendanceRecords[recordKey] || loadAttendanceForSession(assignmentId, session.id);
          const values = Object.values(record || {});
          if (!values.length) return null;
          const presentCount = values.filter((value) => value === 'present').length;
          return (presentCount / values.length) * 100;
        })
        .filter((value) => value !== null);

      const marksList = getTestMarksListData(assignmentId);
      const scoreValues = marksList.flatMap((item) => {
        const recordKey = `${assignmentId}-${item.testName}`;
        const record = testMarksRecords[recordKey] || loadTestMarksForSession(assignmentId, item.testName);
        const values = Object.values(record || {});
        if (!values.length) return [];
        const total = values.reduce((sum, value) => sum + Number(value || 0), 0);
        const average = total / values.length;
        return [average / item.maxMarks * 100];
      });

      const avgAttendance = attendanceValues.length
        ? attendanceValues.reduce((sum, value) => sum + value, 0) / attendanceValues.length
        : 0;

      const avgMarks = scoreValues.length
        ? scoreValues.reduce((sum, value) => sum + value, 0) / scoreValues.length
        : 0;

      return {
        name: courseItem.course?.title || 'Assigned Course',
        students: courseStudents.length,
        batch: courseItem.batch || 'All Batches',
        modules: Array.isArray(courseItem.course?.modules_data) ? courseItem.course.modules_data.length : 0,
        attendance: Math.round(avgAttendance),
        marks: Math.round(avgMarks),
      };
    });
  }, [assignedCourses, attendanceRecords, attendanceSessions, facultyStudents, testMarksRecords]);

  const facultyAnalyticsSummary = useMemo(() => {
    const totalStudents = facultyStudents.length;
    const totalCourses = assignedCourses.length;
    const uniqueBatches = new Set((assignedCourses || []).map((course) => course.batch || 'All Batches')).size;
    const totalModules = assignedCourses.reduce((sum, course) => sum + (Array.isArray(course.course?.modules_data) ? course.course.modules_data.length : 0), 0);
    const avgAttendance = courseAnalytics.length
      ? Math.round(courseAnalytics.reduce((sum, item) => sum + item.attendance, 0) / courseAnalytics.length)
      : 0;
    const avgMarks = courseAnalytics.length
      ? Math.round(courseAnalytics.reduce((sum, item) => sum + item.marks, 0) / courseAnalytics.length)
      : 0;

    return {
      totalStudents,
      totalCourses,
      uniqueBatches,
      totalModules,
      avgAttendance,
      avgMarks,
    };
  }, [assignedCourses, courseAnalytics, facultyStudents]);

  const performanceDistribution = useMemo(() => {
    const ranges = [
      { range: '0–39%', students: 0, fill: '#ef4444' },
      { range: '40–59%', students: 0, fill: '#f97316' },
      { range: '60–79%', students: 0, fill: '#f59e0b' },
      { range: '80–89%', students: 0, fill: '#22c55e' },
      { range: '90–100%', students: 0, fill: '#0ea5e9' },
    ];
    const studentScores = {};

    assignedCourses.forEach((courseItem) => {
      const assignmentId = courseItem.id;
      getTestMarksListData(assignmentId).forEach((test) => {
        const maxMarks = Number(test.maxMarks);
        if (!Number.isFinite(maxMarks) || maxMarks <= 0) return;
        const recordKey = `${assignmentId}-${test.testName}`;
        const record = testMarksRecords[recordKey] || loadTestMarksForSession(assignmentId, test.testName);
        Object.entries(record || {}).forEach(([studentId, value]) => {
          const marks = Number(value);
          if (!Number.isFinite(marks)) return;
          if (!studentScores[studentId]) studentScores[studentId] = [];
          studentScores[studentId].push(Math.max(0, Math.min(100, (marks / maxMarks) * 100)));
        });
      });
    });

    Object.values(studentScores).forEach((scores) => {
      if (!scores.length) return;
      const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
      const rangeIndex = average < 40 ? 0 : average < 60 ? 1 : average < 80 ? 2 : average < 90 ? 3 : 4;
      ranges[rangeIndex].students += 1;
    });

    return ranges;
  }, [assignedCourses, testMarksRecords]);

  const attendanceDistribution = useMemo(() => {
    const byDate = {};
    assignedCourses.forEach((courseItem) => {
      const students = getStudentsForAssignment(courseItem);
      const totalStudents = students.length;
      if (!totalStudents) return;
      const sessions = attendanceSessions[courseItem.id] || loadAttendanceSessions(courseItem.id);
      sessions.forEach((session) => {
        if (!session?.date) return;
        const recordKey = `${courseItem.id}-${session.id}`;
        const records = attendanceRecords[recordKey] || loadAttendanceForSession(courseItem.id, session.id);
        const attended = students.reduce((count, student) => {
          const studentId = String(getAttendanceStudentId(student));
          return count + (String(records?.[studentId] || '').toLowerCase() === 'present' ? 1 : 0);
        }, 0);
        if (!byDate[session.date]) {
          byDate[session.date] = { date: session.date, totalStudents: 0, attendedStudents: 0, sessions: 0 };
        }
        byDate[session.date].totalStudents += totalStudents;
        byDate[session.date].attendedStudents += attended;
        byDate[session.date].sessions += 1;
      });
    });
    return Object.values(byDate)
      .sort((first, second) => first.date.localeCompare(second.date))
      .map((item) => ({ ...item, label: formatAttendanceDate(item.date) }));
  }, [assignedCourses, attendanceRecords, attendanceSessions, facultyStudents]);

  const [selectedAttendanceDate, setSelectedAttendanceDate] = useState('all');
  const visibleAttendanceDistribution = selectedAttendanceDate === 'all'
    ? attendanceDistribution
    : attendanceDistribution.filter((item) => item.date === selectedAttendanceDate);

  const fetchTestMarksForAssignment = async (assignmentId) => {
    if (!assignmentId) return;
    try {
      const res = await api.get(`/faculty/lms/assigned/${assignmentId}/test_marks`);
      const records = Array.isArray(res.data) ? res.data : [];
      const listMap = new Map();
      const nextRecords = {};

      records.forEach(record => {
        listMap.set(record.test_name, {
          testName: record.test_name,
          maxMarks: record.max_marks
        });

        const recordKey = `${assignmentId}-${record.test_name}`;
        nextRecords[recordKey] = {
          ...(nextRecords[recordKey] || {}),
          [record.student_id]: record.marks_obtained
        };
      });

      const list = Array.from(listMap.values());
      localStorage.setItem(getTestMarksListKey(assignmentId), JSON.stringify(list));
      Object.entries(nextRecords).forEach(([recordKey, recordsForTest]) => {
        const testName = recordKey.replace(`${assignmentId}-`, '');
        localStorage.setItem(getTestMarksStorageKey(assignmentId, testName), JSON.stringify(recordsForTest));
      });

      setTestMarksList(prev => ({ ...prev, [assignmentId]: list }));
      setTestMarksRecords(prev => ({ ...prev, ...nextRecords }));
      setFullTestReports(prev => ({ ...prev, [assignmentId]: records }));
    } catch (err) {
      console.error("Error loading test marks records", err);
    }
  };

  const addTestMarksSession = (assignmentId, students = []) => {
    if (!assignmentId || !newTestName || !newMaxMarks) return;
    const currentList = getTestMarksListData(assignmentId);
    if (currentList.some(item => item.testName === newTestName)) return;

    const nextList = [
      ...currentList,
      {
        testName: newTestName,
        maxMarks: Number(newMaxMarks)
      }
    ];

    saveTestMarksList(assignmentId, nextList);

    const defaultRecords = students.map(student => [getAttendanceStudentId(student), 0]);
    const nextRecords = Object.fromEntries(defaultRecords);
    localStorage.setItem(getTestMarksStorageKey(assignmentId, newTestName), JSON.stringify(nextRecords));
    setTestMarksRecords(prev => ({ ...prev, [`${assignmentId}-${newTestName}`]: nextRecords }));
    setTestMarksSaveStatus('');
    setNewTestName('');
  };

  const removeTestMarksSession = async (assignmentId, testName) => {
    const nextList = getTestMarksListData(assignmentId).filter(item => item.testName !== testName);
    localStorage.removeItem(getTestMarksStorageKey(assignmentId, testName));
    saveTestMarksList(assignmentId, nextList);
    setTestMarksRecords(prev => {
      const nextRecords = { ...prev };
      delete nextRecords[`${assignmentId}-${testName}`];
      return nextRecords;
    });
    setTestMarksDraftRecords(prev => {
      const nextRecords = { ...prev };
      delete nextRecords[`${assignmentId}-${testName}`];
      return nextRecords;
    });
    try {
      await api.delete(`/faculty/lms/assigned/${assignmentId}/test_marks`, {
        params: { test_name: testName }
      });
    } catch (err) {
      console.error("Error deleting test marks session", err);
    }
  };

  const getTestMarksStatus = (assignmentId, testName, studentId) => {
    const recordKey = `${assignmentId}-${testName}`;
    const draftRecords = testMarksDraftRecords[recordKey];
    if (draftRecords && Object.prototype.hasOwnProperty.call(draftRecords, studentId)) {
      return draftRecords[studentId];
    }
    const storedRecords = testMarksRecords[recordKey] || loadTestMarksForSession(assignmentId, testName);
    return storedRecords[studentId] || 0;
  };

  const updateTestMarksStatus = (assignmentId, testName, studentId, marks) => {
    const recordKey = `${assignmentId}-${testName}`;
    const currentRecords = testMarksDraftRecords[recordKey] || testMarksRecords[recordKey] || loadTestMarksForSession(assignmentId, testName);
    const nextRecords = { ...currentRecords, [studentId]: Number(marks) };
    setTestMarksDraftRecords(prev => ({ ...prev, [recordKey]: nextRecords }));
    setTestMarksSaveStatus('unsaved');
  };

  const saveVisibleTestMarks = async (assignmentId, students, list) => {
    if (!assignmentId || list.length === 0 || students.length === 0) return;
    const records = list.flatMap(item => (
      students.map(student => {
        const studentId = getAttendanceStudentId(student);
        return {
          student_id: studentId,
          test_name: item.testName,
          max_marks: item.maxMarks,
          marks_obtained: getTestMarksStatus(assignmentId, item.testName, studentId)
        };
      })
    ));

    setTestMarksSaveStatus('saving');
    try {
      await api.post(`/faculty/lms/assigned/${assignmentId}/test_marks`, { records });
      const savedRecordMap = {};
      list.forEach(item => {
        const recordKey = `${assignmentId}-${item.testName}`;
        const sessionRecords = {};
        students.forEach(student => {
          const studentId = getAttendanceStudentId(student);
          sessionRecords[studentId] = getTestMarksStatus(assignmentId, item.testName, studentId);
        });
        savedRecordMap[recordKey] = sessionRecords;
        localStorage.setItem(getTestMarksStorageKey(assignmentId, item.testName), JSON.stringify(sessionRecords));
      });
      localStorage.setItem(getTestMarksListKey(assignmentId), JSON.stringify(list));
      setTestMarksRecords(prev => ({ ...prev, ...savedRecordMap }));
      setTestMarksDraftRecords(prev => {
        const nextDraftRecords = { ...prev };
        list.forEach(item => {
          delete nextDraftRecords[`${assignmentId}-${item.testName}`];
        });
        return nextDraftRecords;
      });
      setTestMarksSaveStatus('saved');
    } catch (err) {
      console.error("Error saving test marks", err);
      setTestMarksSaveStatus('error');
    }
  };

  const downloadFacultyTemplate = async (assignmentId, type) => {
    try {
      const response = await api.get(`/faculty/lms/assigned/${assignmentId}/${type}-template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = type === 'attendance' ? 'faculty_attendance_template.xlsx' : 'faculty_test_marks_template.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      triggerNotification('error', 'Template download failed', 'Could not download the workbook template.');
    }
  };

  const getUploadError = (err) => {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.errors?.length) return detail.errors.join(' | ');
    return 'The workbook could not be uploaded.';
  };

  const uploadFacultyWorkbook = async (assignmentId, type, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    const isAttendance = type === 'attendance';
    const setStatus = isAttendance ? setAttendanceUploadStatus : setTestMarksUploadStatus;
    setStatus('uploading');
    try {
      const response = await api.post(`/faculty/lms/assigned/${assignmentId}/${type}-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (isAttendance) {
        await fetchAttendanceForAssignment(assignmentId);
      } else {
        await fetchTestMarksForAssignment(assignmentId);
      }
      setStatus('uploaded');
      const count = response.data?.records_saved || 0;
      triggerNotification('success', 'Workbook uploaded', `${count} ${isAttendance ? 'attendance records' : 'test marks'} saved.`);
    } catch (err) {
      setStatus('error');
      triggerNotification('error', 'Workbook upload failed', getUploadError(err));
    }
  };

  const getFileUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:')) {
      return url;
    }
    const base = api.defaults.baseURL ? api.defaults.baseURL.replace(/\/$/, '') : '';
    return url.startsWith('/') ? `${base}${url}` : `${base}/${url}`;
  };

  const getOptionString = (option) => {
    if (option === null || option === undefined) return '';
    if (typeof option === 'string') return option;
    if (typeof option === 'object') {
      return option.text || option.option || option.label || option.value || String(Object.values(option)[0] || '');
    }
    return String(option);
  };

  const isCorrectQuizOption = (question, option, optionIndex) => {
    const answer = question?.answer;
    const optionText = getOptionString(option).trim().toLowerCase();
    const parsed = getQuizAnswerIndex(question);
    if (parsed !== -1) return parsed === optionIndex;
    return String(answer) === String(optionIndex) ||
      String(answer) === String(optionIndex + 1) ||
      String(answer).trim().toLowerCase() === optionText;
  };

  const getQuizAnswerIndex = (question) => {
    const answer = question?.answer;
    const optionCount = Array.isArray(question?.options) ? question.options.length : 0;
    if (answer === null || answer === undefined) return -1;

    const normalizeIndex = (n) => {
      if (!Number.isFinite(n)) return -1;
      if (Number.isInteger(n) && n >= 0 && (!optionCount || n < optionCount)) return n;
      if (Number.isInteger(n) && optionCount && n >= 1 && n <= optionCount) return n - 1;
      return -1;
    };

    if (typeof answer === 'number') return normalizeIndex(answer);

    const raw = String(answer).trim();
    if (!raw) return -1;

    if (/^\d+$/.test(raw)) {
      const n = parseInt(raw, 10);
      if (optionCount && n >= 1 && n <= optionCount) return n - 1;
      return normalizeIndex(n);
    }

    const optionLetterMatch = raw.match(/^\s*([A-Za-z])\s*[).:-]?\s*$/);
    if (optionLetterMatch) {
      const idx = optionLetterMatch[1].toUpperCase().charCodeAt(0) - 65;
      return normalizeIndex(idx);
    }

    const optionPrefixMatch = raw.match(/^\s*option\s*([A-Za-z])\b/i);
    if (optionPrefixMatch) {
      const idx = optionPrefixMatch[1].toUpperCase().charCodeAt(0) - 65;
      return normalizeIndex(idx);
    }

    return -1;
  };

  const normalizeQuizQuestions = (questions) => {
    if (!Array.isArray(questions) || questions.length === 0) return Array.isArray(questions) ? questions : [];
    let changed = false;
    const next = questions.map((q) => {
      if (!q) return q;
      if (q.type && q.type !== 'mcq') return q;
      if (!Array.isArray(q.options) || q.options.length === 0) return q;
      const idx = getQuizAnswerIndex(q);
      if (idx !== -1 && q.answer !== idx) {
        changed = true;
        return { ...q, answer: idx };
      }
      if (typeof q.answer === 'string' && Array.isArray(q.options)) {
        const target = q.answer.trim().toLowerCase();
        const found = q.options.findIndex(opt => getOptionString(opt).trim().toLowerCase() === target);
        if (found !== -1 && q.answer !== found) {
          changed = true;
          return { ...q, answer: found };
        }
      }
      return q;
    });
    return changed ? next : questions;
  };

  const renderContentSummary = (content, index) => {
    const text = content.text_content || content.body || content.text || content.description || '';
    const url = content.content_url || content.url || content.file_url || content.link || '';
    const fileUrl = getFileUrl(url);
    const materialId = content.id || `material-${index}`;
    const isOpen = !!openMaterials[materialId];

    return (
      <div key={content.id || index} style={{ padding: '14px', borderRadius: '10px', border: '1px solid var(--faculty-border)', background: 'var(--faculty-panel-soft)' }}>
        <button
          type="button"
          onClick={(e) => {
            if (fileUrl || text) {
              e.stopPropagation();
              setOpenMaterials(prev => ({ ...prev, [materialId]: !prev[materialId] }));
            }
          }}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            color: (fileUrl || text) ? 'var(--faculty-accent)' : 'var(--faculty-text)',
            fontWeight: 700,
            marginBottom: '0',
            background: 'none', border: 'none', padding: 0,
            cursor: (fileUrl || text) ? 'pointer' : 'default',
            textAlign: 'left',
            fontFamily: 'inherit',
            fontSize: '15px'
          }}
        >
          <FileText size={16} />
          {content.title || `Material ${index + 1}`}
          <span style={{ color: 'var(--faculty-muted)', fontWeight: 600 }}>({content.type || 'text'})</span>
        </button>
        {(fileUrl || text) && isOpen && (
          <div
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
            onClick={(e) => { e.stopPropagation(); setOpenMaterials(prev => ({ ...prev, [materialId]: false })); }}
          >
            <div
              style={{ position: 'relative', width: '75vw', background: 'var(--faculty-panel)', padding: '24px', borderRadius: '12px', border: '1px solid var(--faculty-border)', boxShadow: '0 10px 30px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setOpenMaterials(prev => ({ ...prev, [materialId]: false }))}
                style={{ position: 'absolute', top: '16px', right: '20px', background: 'var(--faculty-panel-soft)', border: '1px solid var(--faculty-border)', color: 'var(--faculty-text)', fontSize: '20px', cursor: 'pointer', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}
                aria-label="Close"
              >
                &times;
              </button>
              <h3 style={{ marginTop: 0, color: 'var(--faculty-text)', marginBottom: text ? '12px' : '20px', paddingRight: '40px' }}>{content.title || `Material ${index + 1}`}</h3>
              {text && <p style={{ color: 'var(--faculty-muted)', margin: 0, lineHeight: 1.55, whiteSpace: 'pre-wrap', marginBottom: fileUrl ? '20px' : '0' }}>{text}</p>}
              {fileUrl && (
                <div style={{ width: '100%', height: '75vh', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: String(content.type || '').toLowerCase().includes('video') ? '#000' : '#fff', borderRadius: '8px', overflow: 'hidden' }}>
                  {String(content.type || '').toLowerCase().includes('video') ? (
                    <video src={fileUrl} controls autoPlay style={{ width: '100%', height: '100%', outline: 'none' }} />
                  ) : String(content.type || '').toLowerCase().includes('image') || String(content.type || '').toLowerCase() === 'img' ? (
                    <img src={fileUrl} alt={content.title || 'material'} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : String(content.type || '').toLowerCase().includes('audio') ? (
                    <audio src={fileUrl} controls autoPlay style={{ width: '100%', margin: '40px 20px' }} />
                  ) : (
                    <iframe src={fileUrl.includes('#') ? fileUrl : `${fileUrl}#view=FitH`} style={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#fff' }} title={content.title || 'Material'} />
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleGenerateTestBuilder = async (course) => {
    requireAiDisclaimer('AI Test Builder', () => _runGenerateTestBuilder(course));
  };

  const _runGenerateTestBuilder = async (course) => {
    setTestBuilderLoading(true);
    try {
      const res = await api.post('/courses/test-builder/generate', {
        assessment_type: testBuilderType,
        count: testBuilderCount,
        course_title: course?.title || '',
        course_description: course?.description || course?.overview || '',
        topic: course?.title || ''
      });
      if (res.data && res.data.items) {
        setTestBuilderItems(prev => [...prev, ...res.data.items]);
      }
    } catch (err) {
      console.error("Test builder AI generation error:", err);
      triggerNotification('error', 'Generation Failed', 'Could not generate questions. Please verify your connection or try adding manually.');
    } finally {
      setTestBuilderLoading(false);
    }
  };

  const handleAddManualItem = () => {
    const newId = `manual-${Date.now()}`;
    let newItem = {};
    if (testBuilderType === 'mcq') {
      newItem = {
        id: newId,
        type: 'mcq',
        question: 'New MCQ Question Prompt',
        options: ['Option A', 'Option B', 'Option C', 'Option D'],
        answer: 0,
        marks: 5
      };
    } else if (testBuilderType === 'coding') {
      newItem = {
        id: newId,
        type: 'coding',
        title: 'New Coding Challenge',
        question: 'Write a program to solve...',
        starter_code: '# Write code here\n',
        sample_test_case: 'Input: 5\nOutput: 25',
        marks: 10
      };
    } else {
      newItem = {
        id: newId,
        type: 'conceptual',
        question: 'Explain the core principles of...',
        ideal_answer: 'Sample conceptual solution expected...',
        rubric_keywords: ['core principle', 'key concept', 'application'],
        marks: 10
      };
    }
    setTestBuilderItems([...testBuilderItems, newItem]);
  };

  const handleUpdateTestBuilderItem = (index, field, value) => {
    const updated = [...testBuilderItems];
    updated[index] = { ...updated[index], [field]: value };
    setTestBuilderItems(updated);
  };

  const handleUpdateMcqOption = (itemIdx, optIdx, val) => {
    const updated = [...testBuilderItems];
    const newOpts = [...(updated[itemIdx].options || ['', '', '', ''])];
    newOpts[optIdx] = val;
    updated[itemIdx] = { ...updated[itemIdx], options: newOpts };
    setTestBuilderItems(updated);
  };

  const handleDeleteTestBuilderItem = (index) => {
    setTestBuilderItems(testBuilderItems.filter((_, idx) => idx !== index));
  };

  const handlePushTestToMarks = async (assignmentId, assignedStudents) => {
    if (testBuilderItems.length === 0) {
      triggerNotification('error', 'No Questions Added', 'Please add or generate at least one question before assigning the test.');
      return;
    }
    const totalMarks = testBuilderItems.reduce((acc, it) => acc + (Number(it.marks) || 0), 0);
    const testPayload = {
      testName: testBuilderTitle || "Faculty Assigned Ability Test",
      maxMarks: totalMarks > 0 ? totalMarks : 100,
      type: testBuilderType,
      courseId: selectedAssignment?.course_id || selectedAssignment?.course?.id,
      courseTitle: selectedAssignment?.course?.title,
      questions: testBuilderItems,
      assignedAt: new Date().toISOString()
    };

    // 1. Save to database (persistent)
    try {
      await api.post(`/faculty/lms/assigned/${assignmentId}/save_test`, testPayload);
    } catch (err) {
      console.warn("DB save failed, using localStorage fallback:", err);
    }

    // 2. Also cache in localStorage for instant access
    const cId = selectedAssignment?.course_id || selectedAssignment?.course?.id;
    if (cId) {
      localStorage.setItem(`faculty_assigned_test_${cId}`, JSON.stringify(testPayload));
    }
    if (selectedAssignment?.course?.title) {
      localStorage.setItem(`faculty_assigned_test_title_${selectedAssignment?.course?.title}`, JSON.stringify(testPayload));
    }
    localStorage.setItem('latest_faculty_assigned_test', JSON.stringify(testPayload));

    setNewTestName(testBuilderTitle || "Faculty Assigned Ability Test");
    setNewMaxMarks(totalMarks > 0 ? totalMarks : 100);
    addTestMarksSession(assignmentId, assignedStudents);
    triggerNotification(
      'success',
      'Assessment Assigned Successfully!',
      `"${testPayload.testName}" is now live on the Student Dashboard under 'Faculty Assigned Test'.`
    );
    setCourseDetailSection('testMarks');
  };

  const startEditFinalAssessment = (course) => {
    setFinalAssessmentDraft(normalizeQuizQuestions(course?.quiz_questions || []));
    setIsEditingFinalAssessment(true);
    setFinalAssessmentSaveState('');
  };

  const cancelEditFinalAssessment = () => {
    setIsEditingFinalAssessment(false);
    setFinalAssessmentDraft([]);
    setFinalAssessmentSaveState('');
  };

  const updateFinalAssessmentQuestion = (questionIndex, field, value) => {
    setFinalAssessmentDraft(prev => {
      const next = Array.isArray(prev) ? [...prev] : [];
      next[questionIndex] = { ...(next[questionIndex] || {}), [field]: value };
      return next;
    });
  };

  const updateFinalAssessmentOption = (questionIndex, optionIndex, value) => {
    setFinalAssessmentDraft(prev => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const q = next[questionIndex] || {};
      const opts = Array.isArray(q.options) ? [...q.options] : ['', '', '', ''];
      opts[optionIndex] = value;
      next[questionIndex] = { ...q, options: opts };
      return next;
    });
  };

  const updateFinalAssessmentAnswer = (questionIndex, optionIndex) => {
    setFinalAssessmentDraft(prev => {
      const next = Array.isArray(prev) ? [...prev] : [];
      const q = next[questionIndex] || {};
      next[questionIndex] = { ...q, answer: optionIndex };
      return next;
    });
  };

  const saveFinalAssessment = async (course) => {
    if (!course?.id) return;
    setFinalAssessmentSaveState('saving');
    try {
      const payload = {
        title: course.title || '',
        description: course.description || '',
        category: course.category || '',
        rating: Number.isFinite(course.rating) ? course.rating : 4.5,
        students_count: Number.isFinite(course.students_count) ? course.students_count : 0,
        hours: Number.isFinite(course.hours) ? course.hours : 10,
        is_ai_generated: course.is_ai_generated !== false,
        is_expert_validated: !!course.is_expert_validated,
        image_url: course.image_url || null,
        modules_data: Array.isArray(course.modules_data) ? course.modules_data : [],
        quiz_questions: normalizeQuizQuestions(finalAssessmentDraft),
        course_outcomes: Array.isArray(course.course_outcomes) ? course.course_outcomes : [],
        prerequisites: Array.isArray(course.prerequisites) ? course.prerequisites : []
      };
      const res = await api.put(`/admin/courses/${course.id}`, payload);
      const updatedCourse = res.data;
      setAssignedCourses(prev => prev.map(item => {
        if (item.id !== selectedAssignment?.id) return item;
        return { ...item, course: updatedCourse };
      }));
      setSelectedAssignment(prev => {
        if (!prev) return prev;
        return { ...prev, course: updatedCourse };
      });
      setFinalAssessmentSaveState('saved');
      setIsEditingFinalAssessment(false);
      triggerNotification('success', 'Final Assessment Saved', 'Final assessment questions have been successfully updated.');
    } catch (err) {
      console.error('Error saving final assessment', err);
      setFinalAssessmentSaveState('error');
      triggerNotification('error', 'Save Failed', 'Could not save final assessment changes. Please try again.');
    }
  };


  const renderReportsPanel = (course, assignment, assignedStudents) => {
    const marksList = fullTestReports[assignment.id] || [];
    const testMarksMap = {};
    
    // Build a map of all student submissions regardless of test_name
    // (test_name filter was causing Pending to show even after submission)
    marksList.forEach(m => {
      // If student already has a record, prefer the one with ai_analysis
      if (!testMarksMap[m.student_id] || m.ai_analysis) {
        testMarksMap[m.student_id] = m;
      }
    });

    return (
      <div className="faculty-panel">
        <div className="faculty-attendance-head" style={{ marginBottom: '20px' }}>
          <div>
            <h3>AI Analysis Reports</h3>
            <p className="faculty-muted">
              View submissions for: <strong>{assignment.faculty_test_name || 'Faculty Assigned Test'}</strong> &mdash; AI generated analysis.
            </p>
          </div>
        </div>

        <div className="faculty-reports-table-wrap">
          <table className="faculty-reports-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Submission Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {assignedStudents.map(student => {
                // student.id = StudentProfile ID, student.user_id = User ID
                // marks are stored by user_id (current_user.id on submission)
                const markRecord = testMarksMap[student.user_id];
                const submitted = !!markRecord?.ai_analysis;
                return (
                  <tr key={student.user_id}>
                    <td>
                      <div className="faculty-student-name">
                        {student.full_name || student.email}
                      </div>
                      <div className="faculty-muted" style={{ fontSize: '0.85rem' }}>{student.email}</div>
                    </td>
                    <td>
                      {submitted ? (
                        <span style={{ color: '#10b981', fontWeight: 'bold' }}>Analyzed</span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>Pending</span>
                      )}
                    </td>
                    <td>
                      <button 
                        className="faculty-btn-primary" 
                        disabled={!submitted}
                        style={{ opacity: submitted ? 1 : 0.5 }}
                        onClick={() => setSelectedReportStudent(student)}
                      >
                        View Report
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Modal Overlay for Student Report */}
        {selectedReportStudent && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            padding: '20px'
          }}>
            <div style={{
              background: '#f8fafc',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '900px',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              {/* Modal Header */}
              <div style={{
                background: '#ffffff',
                padding: '16px 24px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                zIndex: 10
              }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#0f172a', fontWeight: '700' }}>Report Details</h3>
                <button 
                  onClick={() => setSelectedReportStudent(null)}
                  style={{
                    background: '#f1f5f9', border: 'none', borderRadius: '50%',
                    width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '20px', color: '#64748b', cursor: 'pointer', transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#0f172a'; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}
                >
                  ×
                </button>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
                {(() => {
                  const rec = testMarksMap[selectedReportStudent.user_id];
                  return rec?.ai_analysis ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {/* Student Info Header */}
                      <div style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', borderRadius: '12px', padding: '20px 24px', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: '600', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Student Report</div>
                          <div style={{ fontSize: '1.4rem', fontWeight: '800' }}>{selectedReportStudent.full_name || selectedReportStudent.email}</div>
                          <div style={{ fontSize: '0.85rem', opacity: 0.8, marginTop: '2px' }}>{selectedReportStudent.email}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '12px', fontWeight: '600', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Score</div>
                          <div style={{ fontSize: '2rem', fontWeight: '900', lineHeight: 1 }}>
                            {rec.marks_obtained != null ? Number(rec.marks_obtained) % 1 === 0 ? Number(rec.marks_obtained) : Number(rec.marks_obtained).toFixed(1) : '—'}
                            <span style={{ fontSize: '1rem', opacity: 0.7 }}>/{rec.max_marks ?? '—'}</span>
                          </div>
                          {rec.marks_obtained != null && rec.max_marks ? (
                            <div style={{ fontSize: '12px', opacity: 0.75, marginTop: '4px', fontWeight: '600' }}>
                              {Math.round((rec.marks_obtained / rec.max_marks) * 100)}%
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* AI Analysis Card */}
                      <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                        <div style={{ background: '#f8fafc', padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '16px' }}>✨</span>
                          <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '15px' }}>AI Analysis</span>
                          <span style={{ marginLeft: 'auto', background: '#eff6ff', color: '#2563eb', fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px' }}>AI Generated</span>
                        </div>
                        <div style={{ padding: '20px 24px' }}>
                          {renderMarkdown(rec.ai_analysis)}
                        </div>
                      </div>

                      {/* Student Answers Card */}
                      <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                        <div style={{ background: '#f8fafc', padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '16px' }}>📝</span>
                          <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '15px' }}>Questions & Answers</span>
                        </div>
                        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                          {(() => {
                            const questions = assignment.faculty_test_questions || [];
                            const answers = rec.student_answers || {};
                            if (questions.length === 0) {
                              return (
                                <div style={{ color: '#64748b', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                                  Question details not available for this submission.
                                </div>
                              );
                            }
                            return questions.map((q, idx) => {
                              const studentRaw = answers[String(idx)] ?? answers[idx];
                              const studentIdx = parseInt(studentRaw);
                              const correctIdx = q.answer;
                              const hasOptions = Array.isArray(q.options) && q.options.length > 0;
                              const isCorrect = hasOptions ? studentIdx === correctIdx : null;
                              const studentAnswerText = hasOptions
                                ? (q.options[studentIdx] ?? `Option ${studentIdx}`)
                                : String(studentRaw ?? 'No Answer');

                              return (
                                <div key={idx} style={{ borderRadius: '10px', border: `1.5px solid ${isCorrect === true ? '#86efac' : isCorrect === false ? '#fca5a5' : '#e2e8f0'}`, overflow: 'hidden' }}>
                                  {/* Question Header */}
                                  <div style={{ padding: '12px 16px', background: isCorrect === true ? '#f0fdf4' : isCorrect === false ? '#fff1f2' : '#f8fafc', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                    <span style={{ background: 'var(--faculty-accent, #05a8f3)', color: '#fff', borderRadius: '6px', padding: '2px 9px', fontSize: '12px', fontWeight: '800', flexShrink: 0, marginTop: '1px' }}>Q{idx + 1}</span>
                                    <span style={{ fontWeight: '600', color: '#0f172a', fontSize: '14px', lineHeight: '1.5', flex: 1 }}>{q.question || '(No question text)'}</span>
                                    {isCorrect === true && <span style={{ flexShrink: 0, color: '#16a34a', fontWeight: '800', fontSize: '18px' }}>✓</span>}
                                    {isCorrect === false && <span style={{ flexShrink: 0, color: '#dc2626', fontWeight: '800', fontSize: '18px' }}>✗</span>}
                                  </div>

                                  {/* Options or Text Answer */}
                                  {hasOptions ? (
                                    <div style={{ padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                      {q.options.map((opt, oIdx) => {
                                        const isStudentChoice = oIdx === studentIdx;
                                        const isCorrectOpt = oIdx === correctIdx;
                                        let bg = '#f8fafc', border = '#e2e8f0', color = '#475569', labelBg = '#e2e8f0', labelColor = '#475569';
                                        if (isCorrectOpt && isStudentChoice) {
                                          bg = '#f0fdf4'; border = '#86efac'; color = '#15803d'; labelBg = '#22c55e'; labelColor = '#fff';
                                        } else if (isStudentChoice && !isCorrectOpt) {
                                          bg = '#fff1f2'; border = '#fca5a5'; color = '#b91c1c'; labelBg = '#ef4444'; labelColor = '#fff';
                                        } else if (isCorrectOpt) {
                                          bg = '#f0fdf4'; border = '#86efac'; color = '#15803d'; labelBg = '#86efac'; labelColor = '#15803d';
                                        }
                                        return (
                                          <div key={oIdx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '7px', background: bg, border: `1px solid ${border}` }}>
                                            <span style={{ background: labelBg, color: labelColor, borderRadius: '5px', padding: '1px 8px', fontSize: '11px', fontWeight: '800', flexShrink: 0 }}>
                                              {String.fromCharCode(65 + oIdx)}
                                            </span>
                                            <span style={{ color, fontSize: '13.5px', fontWeight: isStudentChoice || isCorrectOpt ? '600' : '400', flex: 1 }}>{opt}</span>
                                            {isStudentChoice && !isCorrectOpt && <span style={{ fontSize: '11px', color: '#ef4444', fontWeight: '700' }}>Your Answer</span>}
                                            {isCorrectOpt && !isStudentChoice && <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: '700' }}>Correct Answer</span>}
                                            {isCorrectOpt && isStudentChoice && <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: '700' }}>✓ Correct</span>}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <div style={{ padding: '10px 16px' }}>
                                      <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>Student's Answer:</div>
                                      <div style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: '7px', border: '1px solid #e2e8f0', color: '#334155', fontSize: '14px' }}>{studentAnswerText}</div>
                                    </div>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#64748b', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>📋</div>
                      <div style={{ fontWeight: '600', marginBottom: '6px' }}>No Submission Found</div>
                      <div style={{ fontSize: '14px' }}>This student hasn't submitted the Internal Assessment yet.</div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTestBuilderPanel = (course, assignment, assignedStudents) => {
    const totalMarks = testBuilderItems.reduce((acc, it) => acc + (Number(it.marks) || 0), 0);

    return (
      <div className="faculty-panel" style={{ padding: '28px', background: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
        {/* Banner */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid #f1f5f9' }}>
          <div>
            <h3 style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a', margin: '0 0 6px 0' }}>
              Test & Assessment Builder
            </h3>
            <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 12px 0', maxWidth: '640px', lineHeight: '1.5' }}>
              Create rich assessments for <strong>{course?.title || 'this course'}</strong>. Add questions manually or let AI generate up to 20 multiple-choice, conceptual, or coding challenges tailored to your course overview.
            </p>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#eff6ff', color: '#2563eb', padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '700' }}>
              <Sparkles size={15} /> Powered by AI & Manual Customization
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Assigned Batch</div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>{assignment?.batch || 'All Batches'}</div>
          </div>
        </div>

        {/* Previously Saved Test Notice */}
        {testBuilderItems.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px' }}>
            <span style={{ fontSize: '18px' }}>✅</span>
            <div style={{ flex: 1 }}>
              <strong style={{ color: '#15803d', fontSize: '14px' }}>Previously Assigned Test Loaded</strong>
              <p style={{ margin: 0, fontSize: '12px', color: '#166534' }}>
                {testBuilderItems.length} question{testBuilderItems.length !== 1 ? 's' : ''} loaded from the database.
                You can edit, add, or remove questions below, then click <strong>Assign Assessment</strong> again to save updates.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Clear all loaded questions and start fresh?')) {
                  setTestBuilderItems([]);
                  localStorage.removeItem('saved_test_builder_items');
                  localStorage.removeItem('latest_faculty_assigned_test');
                }
              }}
              style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid #bbf7d0', background: '#fff', color: '#15803d', fontWeight: '700', fontSize: '12px', cursor: 'pointer' }}
            >
              Clear & Start Fresh
            </button>
          </div>
        )}

        {/* Controls Block */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.8fr', gap: '20px', marginBottom: '28px', background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          {/* Assessment Type Selector */}
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
              1. Select Assessment Type
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { id: 'mcq', label: 'Multiple Choice Quiz (MCQ)', icon: CheckSquare, desc: 'Auto-graded 4-option questions' },
                { id: 'conceptual', label: 'Conceptual Questions', icon: HelpCircle, desc: 'Short-answer questions with semantic rubric' }
              ].map(t => {
                const IconComp = t.icon;
                const isSelected = testBuilderType === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTestBuilderType(t.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px',
                      borderRadius: '10px', border: isSelected ? '2px solid #2563eb' : '1px solid #cbd5e1',
                      background: isSelected ? '#eff6ff' : '#ffffff', cursor: 'pointer', textAlign: 'left',
                      transition: 'all 0.15s'
                    }}
                  >
                    <IconComp size={18} color={isSelected ? '#2563eb' : '#64748b'} />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: isSelected ? '#1e40af' : '#0f172a' }}>{t.label}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{t.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* OpenRouter AI Generator Block */}
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
              2. AI Question Generator & Manual Builder
            </label>
            <div style={{ marginBottom: '16px', background: '#ffffff', padding: '12px 14px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>
                Generating Based On Course Topic & Overview:
              </div>
              <div style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a', marginBottom: '4px' }}>
                {course?.title || 'Assigned Course'}
              </div>
              <div style={{ fontSize: '12px', color: '#475569', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {course?.description || course?.overview || 'Comprehensive exploration of curriculum topics and engineering practices.'}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Question Count:</span>
              {[5, 10, 15, 20].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setTestBuilderCount(n)}
                  style={{
                    padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '700', border: 'none',
                    background: testBuilderCount === n ? '#2563eb' : '#e2e8f0',
                    color: testBuilderCount === n ? '#ffffff' : '#475569', cursor: 'pointer'
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => handleGenerateTestBuilder(course)}
                disabled={testBuilderLoading}
                style={{
                  flex: 1, padding: '12px 18px', borderRadius: '8px', border: 'none',
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: '#ffffff',
                  fontWeight: '700', fontSize: '14px', cursor: testBuilderLoading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  boxShadow: '0 4px 12px rgba(37,99,235,0.25)'
                }}
              >
                <Sparkles size={16} />
                {testBuilderLoading ? 'Generating with AI...' : `⚡ Generate ${testBuilderCount} Questions (AI)`}
              </button>
              <button
                type="button"
                onClick={handleAddManualItem}
                style={{
                  padding: '12px 18px', borderRadius: '8px', border: '1px solid #cbd5e1',
                  background: '#ffffff', color: '#0f172a', fontWeight: '700', fontSize: '14px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                }}
              >
                <Plus size={16} /> Add Manually
              </button>
            </div>
          </div>
        </div>

        {/* Questions List */}
        <div style={{ marginBottom: '28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h4 style={{ fontSize: '17px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
              Assessment Items ({testBuilderItems.length} questions • {totalMarks} Total Marks)
            </h4>
            {testBuilderItems.length > 0 && (
              <button
                type="button"
                onClick={() => setTestBuilderItems([])}
                style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
              >
                Clear All
              </button>
            )}
          </div>

          {testBuilderItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 20px', background: '#f8fafc', borderRadius: '12px', border: '2px dashed #cbd5e1' }}>
              <CheckSquare size={44} color="#94a3b8" style={{ marginBottom: '12px' }} />
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>No questions added yet</div>
              <p style={{ fontSize: '13px', color: '#64748b', maxWidth: '420px', margin: '0 auto 16px auto' }}>
                Click "Generate {testBuilderCount} Questions (AI)" above or click "Add Manually" to create your assessment items.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {testBuilderItems.map((item, idx) => (
                <div key={item.id || idx} style={{ padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#ffffff', boxShadow: '0 2px 6px rgba(0,0,0,0.02)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ background: '#eff6ff', color: '#1e40af', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase' }}>
                        Q{idx + 1} • {item.type || 'mcq'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        Marks:
                        <input
                          type="number"
                          value={item.marks || 1}
                          onChange={(e) => handleUpdateTestBuilderItem(idx, 'marks', parseInt(e.target.value, 10) || 1)}
                          style={{ width: '60px', padding: '4px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '700' }}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => handleDeleteTestBuilderItem(idx)}
                        style={{ background: '#fee2e2', color: '#ef4444', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        title="Delete Question"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Fields by Assessment Type */}
                  {item.type === 'mcq' && (
                    <div>
                      <input
                        type="text"
                        value={item.question || ''}
                        onChange={(e) => handleUpdateTestBuilderItem(idx, 'question', e.target.value)}
                        placeholder="Question Prompt"
                        style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', fontWeight: '600', marginBottom: '14px', boxSizing: 'border-box' }}
                      />
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        {(item.options || ['', '', '', '']).map((opt, optIdx) => {
                          const isCorrect = isCorrectQuizOption(item, opt, optIdx);
                          return (
                            <div key={optIdx} className={`faculty-mcq-choice ${isCorrect ? 'correct' : ''}`}>
                              <input
                                type="checkbox"
                                checked={isCorrect}
                                onChange={() => handleUpdateTestBuilderItem(idx, 'answer', optIdx)}
                                title="Mark as Correct Option"
                              />
                              <span className="faculty-mcq-choice-letter">{String.fromCharCode(65 + optIdx)}</span>
                              <input
                                type="text"
                                value={opt}
                                onChange={(e) => handleUpdateMcqOption(idx, optIdx, e.target.value)}
                                placeholder={`Option ${String.fromCharCode(65 + optIdx)}`}
                                className="faculty-mcq-choice-input"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {item.type === 'conceptual' && (
                    <div>
                      <input
                        type="text"
                        value={item.question || ''}
                        onChange={(e) => handleUpdateTestBuilderItem(idx, 'question', e.target.value)}
                        placeholder="Conceptual Question Prompt"
                        style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', fontWeight: '600', marginBottom: '12px', boxSizing: 'border-box' }}
                      />
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Ideal Reference Answer (For AI Rubric Evaluation)</label>
                          <textarea
                            rows={3}
                            value={item.ideal_answer || ''}
                            onChange={(e) => handleUpdateTestBuilderItem(idx, 'ideal_answer', e.target.value)}
                            placeholder="Ideal reference conceptual answer..."
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Rubric Keywords (Comma separated)</label>
                          <input
                            type="text"
                            value={Array.isArray(item.rubric_keywords) ? item.rubric_keywords.join(', ') : (item.rubric_keywords || '')}
                            onChange={(e) => handleUpdateTestBuilderItem(idx, 'rubric_keywords', e.target.value.split(',').map(s => s.trim()))}
                            placeholder="e.g. latency, scalability, RAG"
                            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {item.type === 'coding' && (
                    <div>
                      <input
                        type="text"
                        value={item.title || ''}
                        onChange={(e) => handleUpdateTestBuilderItem(idx, 'title', e.target.value)}
                        placeholder="Coding Challenge Title"
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', fontWeight: '700', marginBottom: '10px', boxSizing: 'border-box' }}
                      />
                      <textarea
                        rows={2}
                        value={item.question || ''}
                        onChange={(e) => handleUpdateTestBuilderItem(idx, 'question', e.target.value)}
                        placeholder="Problem requirements and instructions..."
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', marginBottom: '10px', boxSizing: 'border-box' }}
                      />
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Starter Code Template</label>
                          <textarea
                            rows={4}
                            value={item.starter_code || ''}
                            onChange={(e) => handleUpdateTestBuilderItem(idx, 'starter_code', e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontFamily: 'monospace', fontSize: '12px', background: '#0f172a', color: '#f8fafc', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Sample / Hidden Test Cases</label>
                          <textarea
                            rows={4}
                            value={item.sample_test_case || ''}
                            onChange={(e) => handleUpdateTestBuilderItem(idx, 'sample_test_case', e.target.value)}
                            placeholder="assert solve(input) == output"
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontFamily: 'monospace', fontSize: '12px', background: '#f8fafc', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer Action to push into Test Marks */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>Assessment Title:</span>
            <input
              type="text"
              value={testBuilderTitle}
              onChange={(e) => setTestBuilderTitle(e.target.value)}
              placeholder="e.g. Midterm Assessment"
              style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', fontWeight: '600', width: '260px' }}
            />
          </div>
          <button
            type="button"
            onClick={() => handlePushTestToMarks(assignment?.id, assignedStudents)}
            style={{
              padding: '12px 24px', borderRadius: '8px', border: 'none',
              background: '#10b981', color: '#ffffff', fontWeight: '700', fontSize: '14px',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
              boxShadow: '0 4px 14px rgba(16,185,129,0.3)'
            }}
          >
            <CheckCircle size={17} /> Assign Assessment & Open Test Marks ({totalMarks} Marks)
          </button>
        </div>
      </div>
    );
  };

  const renderCourseDetail = () => {

    const assignment = selectedAssignment;
    const course = assignment?.course || {};
    const modules = Array.isArray(course.modules_data) ? course.modules_data : [];
    const assignedStudents = getStudentsForAssignment(assignment);
    const normalizedStudentSearch = studentTableSearch.trim().toLowerCase();
    const visibleStudents = assignedStudents.filter(student => {
      if (!normalizedStudentSearch) return true;
      return [student.full_name, student.usn, student.branch, student.department]
        .some(value => String(value || '').toLowerCase().includes(normalizedStudentSearch));
    });
    const sortedAttendanceStudents = sortStudentsByUsn(visibleStudents);
    const attendanceSessionList = getAttendanceSessions(assignment?.id);

    return (
      <div className="faculty-course-detail">
        <button
          className="faculty-back-btn"
          onClick={() => setSelectedAssignment(null)}
        >
          <ArrowLeft size={16} /> Back to My Courses
        </button>

        <div className="faculty-detail-hero">
          <div className="faculty-card-kicker">
            <span>{course.category || 'Curriculum'}</span>
            <span>{assignment.batch || 'All Batches'}</span>
          </div>
          <h2>{course.title || 'Assigned Course'}</h2>
          <div className="faculty-course-meta">
            <span><Users size={15} /> {assignedStudents.length} Students</span>
          </div>

        </div>

        <div className="faculty-detail-tabs" role="tablist" aria-label="Course detail sections">
          <button
            type="button"
            className={courseDetailSection === 'attendance' ? 'active' : ''}
            onClick={() => setCourseDetailSection('attendance')}
          >
            Student Attendance
          </button>
          <button
            type="button"
            className={courseDetailSection === 'testMarks' ? 'active' : ''}
            onClick={() => setCourseDetailSection('testMarks')}
          >
            Test Marks
          </button>
        </div>

        {courseDetailSection === 'attendance' ? (

          <div className={`faculty-panel faculty-attendance-panel ${maximizedLmsSection === 'attendance' ? 'faculty-attendance-panel-maximized' : ''}`}>
            <div className="faculty-attendance-head">
              <div>
                <h3>Student Attendance</h3>
                <p className="faculty-muted">{assignment.batch || 'All Batches'} - {assignedStudents.length} students</p>
              </div>
              <div className="faculty-lms-menu">
                <button
                  type="button"
                  className="faculty-lms-menu-trigger"
                  aria-label="Attendance actions"
                  aria-expanded={openLmsMenu === 'attendance'}
                  onClick={() => setOpenLmsMenu(prev => prev === 'attendance' ? null : 'attendance')}
                >
                  <MoreVertical size={20} />
                </button>
                {openLmsMenu === 'attendance' && (
                  <div className="faculty-lms-menu-dropdown">
                    <button type="button" onClick={() => { downloadFacultyTemplate(assignment.id, 'attendance'); setOpenLmsMenu(null); }}>
                      <Download size={15} /> Template
                    </button>
                    <label>
                      <Upload size={15} /> {attendanceUploadStatus === 'uploading' ? 'Uploading...' : 'Upload Excel'}
                      <input
                        type="file"
                        accept=".xlsx"
                        hidden
                        disabled={attendanceUploadStatus === 'uploading'}
                        onChange={(event) => { uploadFacultyWorkbook(assignment.id, 'attendance', event); setOpenLmsMenu(null); }}
                      />
                    </label>
                  </div>
                )}
              </div>
              <label className="faculty-student-search">
                <span>Search students</span>
                <div className="faculty-student-search-input">
                  <Search size={16} aria-hidden="true" />
                  <input
                    type="search"
                    placeholder="Name or USN"
                    value={studentTableSearch}
                    onChange={(e) => setStudentTableSearch(e.target.value)}
                  />
                </div>
              </label>
              <label>
                Class Date
                <input
                  type="date"
                  value={attendanceDate}
                  onChange={(e) => setAttendanceDate(e.target.value)}
                />
              </label>
              <label>
                Class Time
                <input
                  type="time"
                  value={attendanceTime}
                  onChange={(e) => setAttendanceTime(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="faculty-attendance-add-btn"
                onClick={() => addAttendanceSession(assignment.id, assignedStudents)}
              >
                <Plus size={16} /> Add Class
              </button>
              <button
                type="button"
                className="faculty-attendance-maximize-btn"
                aria-label={maximizedLmsSection === 'attendance' ? 'Exit full screen attendance' : 'Maximize attendance'}
                title={maximizedLmsSection === 'attendance' ? 'Exit full screen' : 'Maximize attendance'}
                onClick={() => setMaximizedLmsSection(prev => prev === 'attendance' ? null : 'attendance')}
              >
                {maximizedLmsSection === 'attendance' ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </button>
            </div>
            {studentsLoading ? (
              <p className="faculty-muted">Loading students...</p>
            ) : assignedStudents.length === 0 ? (
              <div className="faculty-empty-mini">
                <Users size={36} />
                <p>No students found for this assigned batch.</p>
              </div>
            ) : sortedAttendanceStudents.length === 0 ? (
              <div className="faculty-empty-mini">
                <Search size={36} />
                <p>No students match your search.</p>
              </div>
            ) : attendanceSessionList.length === 0 ? (
              <div className="faculty-empty-mini">
                <Calendar size={36} />
                <p>Add a class date and time to start marking attendance.</p>
              </div>
            ) : (
              <>
                <div className="faculty-attendance-table-wrap">
                  <table className="faculty-attendance-table faculty-attendance-grid">
                    <thead>
                      <tr>
                        <th style={{ width: '250px', minWidth: '250px' }}>Student</th>
                        <th style={{ width: '220px', minWidth: '220px' }}>
                          <button
                            type="button"
                            className="faculty-attendance-sort-btn"
                            onClick={() => setAttendanceUsnSort(prev => prev === 'asc' ? 'desc' : 'asc')}
                          >
                            USN / Department
                            <ArrowUpDown size={14} />
                            <span>{attendanceUsnSort === 'asc' ? 'ASC' : 'DESC'}</span>
                          </button>
                        </th>
                        {attendanceSessionList.map(session => (
                          <th key={session.id} className="faculty-attendance-session-head" style={{ width: '140px', minWidth: '140px' }}>
                            <div>
                              <div className="faculty-attendance-session-date">
                                <strong>{formatAttendanceDate(session.date)}</strong>
                                <b className="faculty-attendance-session-period">{formatAttendancePeriod(session.time)}</b>
                              </div>
                              <span>{formatAttendanceTime(session.time) || 'Class session'}</span>
                            </div>
                            <button
                              type="button"
                              aria-label={`Remove class on ${formatAttendanceDate(session.date)} at ${formatAttendanceTime(session.time)}`}
                              onClick={() => removeAttendanceSession(assignment.id, session.id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </th>
                        ))}
                        <th style={{ width: '100%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedAttendanceStudents.map(student => {
                        const attendanceStudentId = getAttendanceStudentId(student);
                        return (
                          <tr key={attendanceStudentId}>
                            <td>
                              <strong>{student.full_name || 'Unknown Student'}</strong>
                              <span><Mail size={12} /> {student.email || 'No email'}</span>
                            </td>
                            <td>{student.usn || 'No USN'} - {student.branch || student.department || student.degree || 'No department'}</td>
                            {attendanceSessionList.map(session => (
                              <td key={session.id}>
                                <label className="faculty-attendance-check">
                                  <input
                                    type="checkbox"
                                    checked={getAttendanceStatus(assignment.id, session.id, attendanceStudentId) === 'present'}
                                    onChange={(e) => updateAttendanceStatus(assignment.id, session.id, attendanceStudentId, e.target.checked ? 'present' : 'absent')}
                                  />
                                  <span>{getAttendanceStatus(assignment.id, session.id, attendanceStudentId) === 'present' ? 'Present' : 'Absent'}</span>
                                </label>
                              </td>
                            ))}
                            <td></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="faculty-attendance-save-row">
                  <button
                    type="button"
                    className="faculty-attendance-save-btn"
                    disabled={attendanceSaveStatus === 'saving'}
                    onClick={() => saveVisibleAttendance(assignment.id, assignedStudents, attendanceSessionList)}
                  >
                    <Save size={16} /> {attendanceSaveStatus === 'saving' ? 'Saving...' : 'Save Attendance'}
                  </button>
                  <AttendanceExportControls assignmentId={assignment.id} batch={assignment.batch || 'All Batches'} />
                  {attendanceSaveStatus === 'unsaved' && <span className="faculty-attendance-save-message pending">Unsaved changes</span>}
                  {attendanceSaveStatus === 'saved' && <span className="faculty-attendance-save-message success">Attendance saved</span>}
                  {attendanceSaveStatus === 'error' && <span className="faculty-attendance-save-message error">Could not save attendance</span>}
                </div>
              </>
            )}
          </div>
        ) : null}

        {courseDetailSection === 'testMarks' && (
          <div className={`faculty-panel faculty-attendance-panel ${maximizedLmsSection === 'testMarks' ? 'faculty-attendance-panel-maximized' : ''}`}>
            <div className="faculty-attendance-head">
              <div>
                <h3>Test Marks</h3>
                <p className="faculty-muted">{assignment.batch || 'All Batches'} - {assignedStudents.length} students</p>
              </div>
              <div className="faculty-lms-menu">
                <button
                  type="button"
                  className="faculty-lms-menu-trigger"
                  aria-label="Test marks actions"
                  aria-expanded={openLmsMenu === 'testMarks'}
                  onClick={() => setOpenLmsMenu(prev => prev === 'testMarks' ? null : 'testMarks')}
                >
                  <MoreVertical size={20} />
                </button>
                {openLmsMenu === 'testMarks' && (
                  <div className="faculty-lms-menu-dropdown">
                    <button type="button" onClick={() => { downloadFacultyTemplate(assignment.id, 'test-marks'); setOpenLmsMenu(null); }}>
                      <Download size={15} /> Template
                    </button>
                    <label>
                      <Upload size={15} /> {testMarksUploadStatus === 'uploading' ? 'Uploading...' : 'Upload Excel'}
                      <input
                        type="file"
                        accept=".xlsx"
                        hidden
                        disabled={testMarksUploadStatus === 'uploading'}
                        onChange={(event) => { uploadFacultyWorkbook(assignment.id, 'test-marks', event); setOpenLmsMenu(null); }}
                      />
                    </label>
                  </div>
                )}
              </div>
              <label className="faculty-student-search">
                <span>Search students</span>
                <div className="faculty-student-search-input">
                  <Search size={16} aria-hidden="true" />
                  <input
                    type="search"
                    placeholder="Name or USN"
                    value={studentTableSearch}
                    onChange={(e) => setStudentTableSearch(e.target.value)}
                  />
                </div>
              </label>
              <label>
                Test Name
                <input
                  type="text"
                  placeholder="e.g. Midterm 1"
                  value={newTestName}
                  onChange={(e) => setNewTestName(e.target.value)}
                />
              </label>
              <label>
                Max Marks
                <input
                  type="number"
                  value={newMaxMarks}
                  onChange={(e) => setNewMaxMarks(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="faculty-attendance-add-btn"
                onClick={() => addTestMarksSession(assignment.id, assignedStudents)}
              >
                <Plus size={16} /> Add Test
              </button>
              <button
                type="button"
                className="faculty-attendance-maximize-btn"
                aria-label={maximizedLmsSection === 'testMarks' ? 'Exit full screen test marks' : 'Maximize test marks'}
                title={maximizedLmsSection === 'testMarks' ? 'Exit full screen' : 'Maximize test marks'}
                onClick={() => setMaximizedLmsSection(prev => prev === 'testMarks' ? null : 'testMarks')}
              >
                {maximizedLmsSection === 'testMarks' ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </button>
            </div>
            {studentsLoading ? (
              <p className="faculty-muted">Loading students...</p>
            ) : assignedStudents.length === 0 ? (
              <div className="faculty-empty-mini">
                <Users size={36} />
                <p>No students found for this assigned batch.</p>
              </div>
            ) : sortedAttendanceStudents.length === 0 ? (
              <div className="faculty-empty-mini">
                <Search size={36} />
                <p>No students match your search.</p>
              </div>
            ) : getTestMarksListData(assignment?.id).length === 0 ? (
              <div className="faculty-empty-mini">
                <FileText size={36} />
                <p>Add a test name and max marks to start entering scores.</p>
              </div>
            ) : (
              <>
                <div className="faculty-attendance-table-wrap">
                  <table className="faculty-attendance-table">
                    <thead>
                      <tr>
                        <th style={{ width: '250px', minWidth: '250px' }}>Student</th>
                        <th style={{ width: '220px', minWidth: '220px' }}>
                          <button
                            type="button"
                            className="faculty-attendance-sort-btn"
                            onClick={() => setAttendanceUsnSort(prev => prev === 'asc' ? 'desc' : 'asc')}
                          >
                            USN / Department
                            <ArrowUpDown size={14} />
                            <span>{attendanceUsnSort === 'asc' ? 'ASC' : 'DESC'}</span>
                          </button>
                        </th>
                        {getTestMarksListData(assignment?.id).map(item => (
                          <th key={item.testName} className="faculty-attendance-session-head" style={{ width: '140px', minWidth: '140px' }}>
                            <div>
                              <strong>{item.testName}</strong>
                              <span>Max: {item.maxMarks}</span>
                            </div>
                            <button
                              type="button"
                              aria-label={`Remove test ${item.testName}`}
                              onClick={() => removeTestMarksSession(assignment.id, item.testName)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </th>
                        ))}
                        <th style={{ width: '100%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedAttendanceStudents.map(student => {
                        const attendanceStudentId = getAttendanceStudentId(student);
                        return (
                          <tr key={attendanceStudentId}>
                            <td>
                              <strong>{student.full_name || 'Unknown Student'}</strong>
                              <span><Mail size={12} /> {student.email || 'No email'}</span>
                            </td>
                            <td>{student.usn || 'No USN'} - {student.branch || student.department || student.degree || 'No department'}</td>
                            {getTestMarksListData(assignment?.id).map(item => (
                              <td key={item.testName}>
                                <input
                                  type="number"
                                  style={{ width: '80px', padding: '6px 8px', border: '1px solid var(--faculty-border)', borderRadius: '6px', background: 'var(--faculty-panel)', color: 'var(--faculty-text)', fontWeight: '600' }}
                                  value={getTestMarksStatus(assignment.id, item.testName, attendanceStudentId)}
                                  onChange={(e) => {
                                    let val = e.target.value;
                                    if (val !== '') {
                                      let numVal = Number(val);
                                      if (numVal > item.maxMarks) numVal = item.maxMarks;
                                      if (numVal < 0) numVal = 0;
                                      val = numVal;
                                    }
                                    updateTestMarksStatus(assignment.id, item.testName, attendanceStudentId, val);
                                  }}
                                  max={item.maxMarks}
                                  min={0}
                                />
                              </td>
                            ))}
                            <td></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="faculty-attendance-save-row">
                  <button
                    type="button"
                    className="faculty-attendance-save-btn"
                    disabled={testMarksSaveStatus === 'saving'}
                    onClick={() => saveVisibleTestMarks(assignment.id, assignedStudents, getTestMarksListData(assignment?.id))}
                  >
                    <Save size={16} /> {testMarksSaveStatus === 'saving' ? 'Saving...' : 'Save Marks'}
                  </button>
                  {testMarksSaveStatus === 'unsaved' && <span className="faculty-attendance-save-message pending">Unsaved changes</span>}
                  {testMarksSaveStatus === 'saved' && <span className="faculty-attendance-save-message success">Marks saved</span>}
                  {testMarksSaveStatus === 'error' && <span className="faculty-attendance-save-message error">Could not save marks</span>}
                </div>
              </>
            )}
          </div>
        )}


      </div>
    );
  };
  const validateProfile = () => {
    const errors = {};
    if (formData.phone_number && !/^\d{10}$/.test(formData.phone_number)) {
      errors.phone_number = "Phone number must contain exactly 10 digits.";
    }
    if (formData.full_name) {
      const name = formData.full_name.trim();
      if (!/^[A-Za-z\s\.]+$/.test(name)) {
        errors.full_name = "Name can only contain letters, spaces, and '.'";
      }
    }
    return errors;
  };

  const formErrors = isEditing ? validateProfile() : {};
  const isFormValid = Object.keys(formErrors).length === 0;

  const handleSaveProfile = async () => {
    if (!isFormValid) return;
    try {
      const res = await api.put('/profile/faculty', formData);
      setProfile({
        ...profile,
        phone_number: res.data.phone_number,
        faculty_profile: res.data.faculty_profile
      });
      setIsEditing(false);
      triggerNotification('success', 'Profile Updated', 'Your profile has been successfully updated.');
    } catch (err) {
      console.error("Error updating profile", err);
      triggerNotification('error', 'Update Failed', err.response?.data?.detail || 'Failed to update profile.');
    }
  };

  const renderAssessmentReportsTab = () => {

    const filteredReports = proctoringReports.filter(r => {
      const isSkill = isSkillAssessmentReport(r);
      const matchesCategory =
        reportsCategoryFilter === 'ALL' ||
        (reportsCategoryFilter === 'SKILL' && isSkill) ||
        (reportsCategoryFilter === 'PRACTICE' && !isSkill);
      if (!matchesCategory) return false;

      const search = reportsSearch.toLowerCase();
      return (
        (r.student_name || '').toLowerCase().includes(search) ||
        (r.student_email || '').toLowerCase().includes(search) ||
        (r.usn || '').toLowerCase().includes(search) ||
        (r.assessment_title || '').toLowerCase().includes(search) ||
        (r.branch || '').toLowerCase().includes(search)
      );
    });

    const skillCount = proctoringReports.filter(r => isSkillAssessmentReport(r)).length;
    const practiceCount = proctoringReports.filter(r => !isSkillAssessmentReport(r)).length;
    const totalViolations = proctoringReports.reduce((acc, r) => acc + (r.violation_count || 0), 0);
    const resolvedCount = proctoringReports.filter(r => r.status === 'RETAKE_GRANTED' || r.status === 'OVERRULED_CLEAN' || r.status === 'CLEAN').length;

    return (
      <div className="unidash-fade-in" style={{ fontFamily: "'Inter', sans-serif" }}>
        {/* Header with Quick Category Filter Pills */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '0 0 6px 0', letterSpacing: '-0.5px' }}>
              Assessment Proctoring Reports
            </h2>
            <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
              Review students disqualified due to automated AI proctoring violations and practice session integrity logs.
            </p>
          </div>

          <div className="faculty-reports-toolbar" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Category filter pills */}
            <div style={{ display: 'flex', background: '#f1f5f9', padding: '4px', borderRadius: '10px', gap: '4px', border: '1px solid #e2e8f0' }}>
              <button
                type="button"
                onClick={() => setReportsCategoryFilter('ALL')}
                style={{
                  padding: '7px 14px',
                  borderRadius: '7px',
                  border: 'none',
                  background: reportsCategoryFilter === 'ALL' ? '#ffffff' : 'transparent',
                  color: reportsCategoryFilter === 'ALL' ? '#0f172a' : '#64748b',
                  fontWeight: reportsCategoryFilter === 'ALL' ? '800' : '600',
                  fontSize: '12.5px',
                  cursor: 'pointer',
                  boxShadow: reportsCategoryFilter === 'ALL' ? '0 2px 5px rgba(0,0,0,0.06)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                All ({proctoringReports.length})
              </button>
              <button
                type="button"
                onClick={() => setReportsCategoryFilter('SKILL')}
                style={{
                  padding: '7px 14px',
                  borderRadius: '7px',
                  border: 'none',
                  background: reportsCategoryFilter === 'SKILL' ? '#ffffff' : 'transparent',
                  color: reportsCategoryFilter === 'SKILL' ? '#0f172a' : '#64748b',
                  fontWeight: reportsCategoryFilter === 'SKILL' ? '800' : '600',
                  fontSize: '12.5px',
                  cursor: 'pointer',
                  boxShadow: reportsCategoryFilter === 'SKILL' ? '0 2px 5px rgba(0,0,0,0.06)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                Skill Assessments ({skillCount})
              </button>
              <button
                type="button"
                onClick={() => setReportsCategoryFilter('PRACTICE')}
                style={{
                  padding: '7px 14px',
                  borderRadius: '7px',
                  border: 'none',
                  background: reportsCategoryFilter === 'PRACTICE' ? '#ffffff' : 'transparent',
                  color: reportsCategoryFilter === 'PRACTICE' ? '#0f172a' : '#64748b',
                  fontWeight: reportsCategoryFilter === 'PRACTICE' ? '800' : '600',
                  fontSize: '12.5px',
                  cursor: 'pointer',
                  boxShadow: reportsCategoryFilter === 'PRACTICE' ? '0 2px 5px rgba(0,0,0,0.06)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                AI Practice Sessions ({practiceCount})
              </button>
            </div>

            <div className="faculty-reports-search" style={{ position: 'relative', width: '240px' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                type="text"
                placeholder="Search student, USN..."
                value={reportsSearch}
                onChange={(e) => setReportsSearch(e.target.value)}
                style={{ width: '100%', padding: '8px 12px 8px 36px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' }}
              />
            </div>

            <button
              type="button"
              className="faculty-reports-refresh"
              onClick={fetchProctoringReports}
              style={{ padding: '8px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#334155', fontWeight: '600', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={{ background: '#ffffff', padding: '18px 20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Skill Disqualifications</span>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Shield size={18} />
              </div>
            </div>
            <div style={{ fontSize: '26px', fontWeight: '800', color: '#991b1b' }}>{skillCount}</div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Formal exams pending review</div>
          </div>

          <div style={{ background: '#ffffff', padding: '18px 20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>AI Practice Logs</span>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#e0e7ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Users size={18} />
              </div>
            </div>
            <div style={{ fontSize: '26px', fontWeight: '800', color: '#3730a3' }}>{practiceCount}</div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>AI Interview practice sessions</div>
          </div>

          <div style={{ background: '#ffffff', padding: '18px 20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Total Flagged Incidents</span>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#fef3c7', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AlertCircle size={18} />
              </div>
            </div>
            <div style={{ fontSize: '26px', fontWeight: '800', color: '#b45309' }}>{totalViolations}</div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Total flagged incident events</div>
          </div>

          <div style={{ background: '#ffffff', padding: '18px 20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Resolved / Retakes</span>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircle size={18} />
              </div>
            </div>
            <div style={{ fontSize: '26px', fontWeight: '800', color: '#166534' }}>{resolvedCount}</div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Audited &amp; resolved by faculty</div>
          </div>
        </div>

        {/* Table of Disqualified Reports */}
        <div style={{ background: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.03)', overflow: 'hidden' }}>
          {reportsLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading proctoring reports...</div>
          ) : filteredReports.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <Shield size={48} style={{ color: '#cbd5e1', marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', marginBottom: '6px' }}>No Reports Found</h3>
              <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
                {reportsSearch ? 'No reports matched your search query.' : 'No proctoring incident reports recorded.'}
              </p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <th style={{ padding: '14px 20px', fontWeight: '700' }}>Student Details</th>
                  <th style={{ padding: '14px 20px', fontWeight: '700' }}>Assessment Title</th>
                  <th style={{ padding: '14px 20px', fontWeight: '700' }}>Violations Count</th>
                  <th style={{ padding: '14px 20px', fontWeight: '700' }}>Incident / Session Date</th>
                  <th style={{ padding: '14px 20px', fontWeight: '700' }}>Status</th>
                  <th style={{ padding: '14px 20px', fontWeight: '700', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((report) => (
                  <tr key={report.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}>
                    <td style={{ padding: '16px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: isSkillAssessmentReport(report) ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '14px', flexShrink: 0 }}>
                          {(report.student_name || 'S').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: '700', color: '#0f172a' }}>{report.student_name}</div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>{report.student_email}</div>
                          <div style={{ fontSize: '11px', color: '#2563eb', fontWeight: '600', marginTop: '2px' }}>
                            <span>USN: {report.usn}</span>
                            <span style={{ margin: '0 6px', color: '#cbd5e1' }}>•</span>
                            <span style={{ color: '#64748b' }}>{report.branch} ({report.academic_batch})</span>
                          </div>
                        </div>
                      </div>
                      {isSkillAssessmentReport(report) && report.retake_reason && (
                        <div style={{ marginTop: '8px', padding: '6px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '11.5px', color: '#1e40af', maxWidth: '320px' }}>
                          <strong style={{ display: 'block', color: '#1d4ed8' }}>📣 Student Appeal:</strong>
                          "{report.retake_reason}"
                          {report.retake_requested_at && (
                            <div style={{ fontSize: '10px', color: '#60a5fa', marginTop: '2px' }}>
                              Submitted: {new Date(report.retake_requested_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '16px 20px', fontWeight: '600', color: '#334155' }}>
                      <div>{report.assessment_title}</div>
                      {isSkillAssessmentReport(report) ? (
                        <span style={{ display: 'inline-block', fontSize: '10px', fontWeight: '700', background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: '10px', marginTop: '4px', border: '1px solid #bfdbfe' }}>
                          Monthly Skill Assessment
                        </span>
                      ) : (
                        <span style={{ display: 'inline-block', fontSize: '10px', fontWeight: '700', background: '#f0fdf4', color: '#166534', padding: '2px 8px', borderRadius: '10px', marginTop: '4px', border: '1px solid #bbf7d0' }}>
                          AI Practice Session
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <span style={{ padding: '4px 10px', borderRadius: '12px', background: '#fee2e2', color: '#991b1b', fontSize: '12.5px', fontWeight: '800' }}>
                        {report.violation_count} Violations
                      </span>
                    </td>
                    <td style={{ padding: '16px 20px', fontSize: '13px', color: '#64748b' }}>
                      {report.created_at ? new Date(report.created_at).toLocaleString() : 'N/A'}
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <span style={{
                        padding: '4px 12px', borderRadius: '20px',
                        background: report.status === 'OVERRULED_CLEAN' || report.status === 'CLEAN' ? '#15803d' : report.status === 'RETAKE_GRANTED' ? '#1d4ed8' : '#7f1d1d',
                        color: '#ffffff', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.04em'
                      }}>
                        {report.status}
                      </span>
                      {isSkillAssessmentReport(report) && report.retake_granted_by_faculty && report.status !== 'RETAKE_GRANTED' && (
                        <div style={{ fontSize: '11px', color: '#16a34a', fontWeight: '700', marginTop: '4px' }}>
                          ✨ Retake Granted
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => handleOpenProctorAudit(report)}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          background: isSkillAssessmentReport(report)
                            ? (report.status === 'OVERRULED_CLEAN' ? '#15803d' : report.status === 'RETAKE_GRANTED' ? '#1d4ed8' : '#0f172a')
                            : '#0f172a',
                          color: '#ffffff',
                          border: 'none',
                          fontSize: '13px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                        }}
                      >
                        {isSkillAssessmentReport(report) ? (
                          <>
                            <ShieldCheck size={14} /> Audit &amp; Verify
                          </>
                        ) : (
                          <>
                            <Eye size={14} /> View Snapshots
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Phase 3 Proctoring Review Studio Modal */}
        {selectedProctorReport && (() => {
          const events = Array.isArray(selectedProctorReport.violations_data) && selectedProctorReport.violations_data.length > 0
            ? selectedProctorReport.violations_data
            : [{ type: 'PROCTORING_FLAG', message: selectedProctorReport.report_summary || 'Proctoring alert flagged', timestamp: 'Session recorded' }];
          const currentEvent = events[activeSnapshotIndex] || events[0];
          const isSkill = isSkillAssessmentReport(selectedProctorReport);

          const scoreMatch = (selectedProctorReport.report_summary || '').match(/Integrity Score\s+(\d+)/i);
          const parsedScore = scoreMatch ? parseInt(scoreMatch[1], 10) : (selectedProctorReport.status === 'CLEAN' ? 100 : selectedProctorReport.status === 'FLAGGED' ? 45 : 75);

          const violationSummaryCounts = events.reduce((acc, ev) => {
            const label = formatViolationType(ev.type);
            acc[label] = (acc[label] || 0) + 1;
            return acc;
          }, {});

          return (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(10px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
              <div style={{ width: '1240px', maxWidth: '96vw', height: '90vh', maxHeight: '900px', background: '#ffffff', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.45)', display: 'flex', flexDirection: 'column' }}>

                {/* Top Studio Header */}
                <div style={{ padding: '16px 24px', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: isSkill ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.2)', border: isSkill ? '1px solid rgba(239,68,68,0.4)' : '1px solid rgba(59,130,246,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isSkill ? '#f87171' : '#60a5fa' }}>
                      <Shield size={24} />
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '11px', fontWeight: '800', background: 'rgba(255,255,255,0.15)', padding: '2px 8px', borderRadius: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#ffffff' }}>
                          {isSkill ? `PROCTORING AUDIT #${selectedProctorReport.id}` : `PRACTICE SESSION LOG #${selectedProctorReport.id}`}
                        </span>
                        <span style={{
                          fontSize: '11px',
                          fontWeight: '800',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          background: selectedProctorReport.status === 'OVERRULED_CLEAN' || selectedProctorReport.status === 'CLEAN' ? '#dcfce7' : selectedProctorReport.status === 'RETAKE_GRANTED' ? '#dbeafe' : '#fee2e2',
                          color: selectedProctorReport.status === 'OVERRULED_CLEAN' || selectedProctorReport.status === 'CLEAN' ? '#15803d' : selectedProctorReport.status === 'RETAKE_GRANTED' ? '#1d4ed8' : '#b91c1c'
                        }}>
                          {selectedProctorReport.status}
                        </span>
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', fontWeight: '600' }}>
                          🕒 {selectedProctorReport.created_at ? new Date(selectedProctorReport.created_at).toLocaleString() : 'Recent'}
                        </span>
                      </div>
                      <div style={{ fontSize: '18px', fontWeight: '800', margin: '3px 0 0 0', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{selectedProctorReport.student_name}</span>
                        <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: '400' }}>•</span>
                        <span style={{ color: '#93c5fd', fontWeight: '700' }}>{selectedProctorReport.assessment_title}</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <span style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>Total Incidents</span>
                      <strong style={{ fontSize: '16px', color: '#f87171', fontWeight: '800' }}>
                        {selectedProctorReport.violation_count || events.length} Flagged
                      </strong>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedProctorReport(null);
                        setIsPlayingSequence(false);
                      }}
                      style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: 'none', color: '#ffffff', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
                      title="Close Audit Window (Esc)"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* Studio 2-Column Command Body */}
                <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: '20px', padding: '20px 24px', background: '#f8fafc', overflow: 'hidden' }}>

                  {/* LEFT COLUMN: Cinema Incident Player & Integrated Timeline Filmstrip */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0, height: '100%', overflow: 'hidden' }}>

                    {/* Integrated Cinema Player Box */}
                    <div style={{ background: '#090d16', borderRadius: '18px', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', boxShadow: '0 12px 36px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>

                      {/* Player Top Navigation Bar */}
                      <div style={{ padding: '12px 18px', background: '#0f172a', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 10px #ef4444' }} />
                          <span style={{ color: '#f8fafc', fontSize: '12.5px', fontWeight: '800', letterSpacing: '0.04em' }}>
                            INCIDENT SNAPSHOT PLAYBACK
                          </span>
                          <span style={{ background: 'rgba(239,68,68,0.2)', color: '#fca5a5', padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', border: '1px solid rgba(239,68,68,0.3)' }}>
                            {formatViolationType(currentEvent.type)}
                          </span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ color: '#94a3b8', fontSize: '12px', fontFamily: 'monospace', background: 'rgba(255,255,255,0.08)', padding: '3px 8px', borderRadius: '6px' }}>
                            Frame {activeSnapshotIndex + 1} of {events.length}
                          </span>
                          {currentEvent.snapshot && (
                            <button
                              type="button"
                              onClick={() => setLightboxSnapshot(currentEvent.snapshot)}
                              style={{ background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.4)', color: '#93c5fd', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11.5px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' }}
                              title="Zoom In Snapshot"
                            >
                              <ZoomIn size={13} /> Inspect
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Screen / Frame Display Canvas */}
                      <div style={{ position: 'relative', flex: 1, minHeight: '260px', background: '#020617', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                        {currentEvent.snapshot ? (
                          <img
                            src={currentEvent.snapshot}
                            alt={`Proctoring Snapshot ${activeSnapshotIndex + 1}`}
                            style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }}
                          />
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', color: '#64748b', padding: '24px' }}>
                            <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: 'rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f87171' }}>
                              <AlertTriangle size={32} />
                            </div>
                            <span style={{ color: '#cbd5e1', fontSize: '15px', fontWeight: '700' }}>
                              {formatViolationType(currentEvent.type)}
                            </span>
                            <span style={{ color: '#94a3b8', fontSize: '12.5px', maxWidth: '360px', textAlign: 'center', lineHeight: 1.5 }}>
                              {currentEvent.message || 'Environmental or browser departure logged during session.'}
                            </span>
                          </div>
                        )}

                        {/* Overhead Incident Status Badge */}
                        <div style={{ position: 'absolute', top: '14px', left: '14px', display: 'flex', gap: '8px' }}>
                          <span style={{ background: 'rgba(220, 38, 38, 0.92)', color: '#ffffff', padding: '5px 12px', borderRadius: '8px', fontSize: '11.5px', fontWeight: '800', letterSpacing: '0.03em', boxShadow: '0 4px 12px rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <AlertCircle size={13} /> {formatViolationType(currentEvent.type)}
                          </span>
                          <span style={{ background: 'rgba(15, 23, 42, 0.9)', color: '#93c5fd', padding: '5px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', border: '1px solid rgba(255,255,255,0.15)' }}>
                            🕒 {currentEvent.timestamp || 'Recorded'}
                          </span>
                        </div>
                      </div>

                      {/* Playback Controls & Incident Caption */}
                      <div style={{ padding: '12px 18px', background: '#0f172a', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, flexWrap: 'wrap', gap: '10px' }}>
                        <div style={{ color: '#f8fafc', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '220px' }}>
                          <span style={{ color: '#38bdf8', fontWeight: '800' }}>Violation:</span>
                          <span style={{ color: '#e2e8f0' }}>{currentEvent.message || 'Proctoring violation event captured.'}</span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '11px', color: '#64748b', marginRight: '6px' }}>
                            ← / → or Space
                          </span>
                          <button
                            type="button"
                            disabled={activeSnapshotIndex === 0}
                            onClick={() => {
                              setActiveSnapshotIndex(prev => Math.max(0, prev - 1));
                              setIsPlayingSequence(false);
                            }}
                            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: activeSnapshotIndex === 0 ? '#475569' : '#f8fafc', padding: '6px 12px', borderRadius: '8px', cursor: activeSnapshotIndex === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '700' }}
                          >
                            <ChevronLeft size={16} /> Prev
                          </button>

                          <button
                            type="button"
                            onClick={() => setIsPlayingSequence(prev => !prev)}
                            style={{ background: isPlayingSequence ? '#dc2626' : '#2563eb', border: 'none', color: '#ffffff', padding: '6px 16px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '700', boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }}
                          >
                            {isPlayingSequence ? <Pause size={14} /> : <Play size={14} />}
                            {isPlayingSequence ? 'Pause' : 'Play Sequence'}
                          </button>

                          <button
                            type="button"
                            disabled={activeSnapshotIndex === events.length - 1}
                            onClick={() => {
                              setActiveSnapshotIndex(prev => Math.min(events.length - 1, prev + 1));
                              setIsPlayingSequence(false);
                            }}
                            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: activeSnapshotIndex === events.length - 1 ? '#475569' : '#f8fafc', padding: '6px 12px', borderRadius: '8px', cursor: activeSnapshotIndex === events.length - 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '700' }}
                          >
                            Next <ChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Integrated Scrubber Filmstrip Deck */}
                    <div style={{ background: '#ffffff', borderRadius: '16px', padding: '12px 16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', flexShrink: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '12.5px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Clock size={14} color="#6366f1" /> Chronological Incident Scrubber ({events.length} frames)
                        </span>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>
                          Click any frame or use arrow keys to scrub
                        </span>
                      </div>

                      <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '4px' }}>
                        {events.map((ev, idx) => {
                          const isCurrent = idx === activeSnapshotIndex;
                          return (
                            <div
                              key={idx}
                              onClick={() => {
                                setActiveSnapshotIndex(idx);
                                setIsPlayingSequence(false);
                              }}
                              style={{
                                flex: '0 0 135px',
                                width: '135px',
                                padding: '8px',
                                borderRadius: '10px',
                                background: isCurrent ? '#0f172a' : '#f8fafc',
                                color: isCurrent ? '#ffffff' : '#334155',
                                border: isCurrent ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                                boxShadow: isCurrent ? '0 4px 14px rgba(59,130,246,0.3)' : 'none'
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '10px', fontWeight: '800', color: isCurrent ? '#60a5fa' : '#64748b' }}>
                                  #{idx + 1}
                                </span>
                                <span style={{ fontSize: '9.5px', color: isCurrent ? '#94a3b8' : '#94a3b8' }}>
                                  {ev.timestamp || 'N/A'}
                                </span>
                              </div>

                              {ev.snapshot ? (
                                <div style={{ width: '100%', height: '52px', borderRadius: '6px', overflow: 'hidden', background: '#020617', margin: '2px 0' }}>
                                  <img src={ev.snapshot} alt="thumb" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                              ) : (
                                <div style={{ width: '100%', height: '52px', borderRadius: '6px', background: isCurrent ? '#1e293b' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '2px 0' }}>
                                  <AlertTriangle size={18} color="#f87171" />
                                </div>
                              )}

                              <div style={{ fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: isCurrent ? '#93c5fd' : '#1e293b' }} title={ev.type}>
                                {formatViolationType(ev.type)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                  </div>

                  {/* RIGHT COLUMN: Candidate Dossier, Integrity Gauge, and Audit Actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', overflowY: 'auto', paddingRight: '2px' }}>

                    {/* Candidate Identity Card */}
                    <div style={{ background: '#ffffff', padding: '16px 18px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '16px' }}>
                          {(selectedProctorReport.student_name || 'S').charAt(0).toUpperCase()}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {selectedProctorReport.student_name}
                          </h4>
                          <span style={{ fontSize: '12px', color: '#64748b', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {selectedProctorReport.student_email}
                          </span>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11.5px', background: '#f8fafc', padding: '10px 12px', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
                        <div>
                          <span style={{ color: '#64748b', display: 'block', fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>USN</span>
                          <strong style={{ color: '#0f172a' }}>{selectedProctorReport.usn || 'N/A'}</strong>
                        </div>
                        <div>
                          <span style={{ color: '#64748b', display: 'block', fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>Department</span>
                          <strong style={{ color: '#0f172a' }}>{selectedProctorReport.branch || 'General'}</strong>
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                          <span style={{ color: '#64748b', display: 'block', fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>Batch Year</span>
                          <strong style={{ color: '#0f172a' }}>{selectedProctorReport.academic_batch || 'Current'}</strong>
                        </div>
                      </div>
                    </div>

                    {/* Integrity Score Meter Card */}
                    <div style={{ background: '#ffffff', padding: '16px 18px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '11px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Integrity Assessment</span>
                        <span style={{
                          fontSize: '11px',
                          fontWeight: '800',
                          padding: '2px 8px',
                          borderRadius: '8px',
                          background: parsedScore >= 80 ? '#dcfce7' : parsedScore >= 60 ? '#fef3c7' : '#fee2e2',
                          color: parsedScore >= 80 ? '#15803d' : parsedScore >= 60 ? '#b45309' : '#b91c1c'
                        }}>
                          {parsedScore >= 80 ? 'HIGH' : parsedScore >= 60 ? 'MODERATE' : 'CRITICAL'}
                        </span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '28px', fontWeight: '900', color: parsedScore >= 80 ? '#16a34a' : parsedScore >= 60 ? '#d97706' : '#dc2626' }}>
                          {parsedScore}%
                        </span>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>Score Finalized</span>
                      </div>

                      {/* Progress Bar */}
                      <div style={{ width: '100%', height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden', marginBottom: '14px' }}>
                        <div style={{
                          width: `${Math.max(5, Math.min(100, parsedScore))}%`,
                          height: '100%',
                          background: parsedScore >= 80 ? '#16a34a' : parsedScore >= 60 ? '#f59e0b' : '#ef4444',
                          transition: 'width 0.5s ease'
                        }} />
                      </div>

                      {/* Incident Breakdown Chips */}
                      <div>
                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>Incident Breakdown</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {Object.entries(violationSummaryCounts).map(([label, count], i) => (
                            <span key={i} style={{ fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '8px', background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0' }}>
                              {label}: <strong style={{ color: '#dc2626' }}>{count}</strong>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Practice Session vs Skill Assessment Auditing */}
                    {!isSkill ? (
                      <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '16px', padding: '16px 18px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0284c7', fontWeight: '800', fontSize: '13px', marginBottom: '6px' }}>
                          <Sparkles size={16} /> Interactive Practice Session
                        </div>
                        <p style={{ margin: 0, fontSize: '12px', color: '#0369a1', lineHeight: 1.5 }}>
                          This is an interactive AI simulation log. Practice sessions are non-punitive with no retake lockouts. Students review these snapshots to improve posture, eye contact, and environment prior to official assessments.
                        </p>
                      </div>
                    ) : (
                      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '16px 18px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                          <ShieldCheck size={18} color="#15803d" />
                          <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
                            Faculty Review &amp; Verdict
                          </h4>
                        </div>

                        {selectedProctorReport.retake_reason && (
                          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontSize: '12px', color: '#1e40af' }}>
                            <strong style={{ display: 'block', color: '#1d4ed8', marginBottom: '2px' }}>Student Appeal:</strong>
                            "{selectedProctorReport.retake_reason}"
                          </div>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                          <label style={{
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: facultyVerdict === 'VERIFIED_DISQUALIFIED' ? '2px solid #dc2626' : '1px solid #cbd5e1',
                            background: facultyVerdict === 'VERIFIED_DISQUALIFIED' ? '#fef2f2' : '#ffffff',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '12.5px',
                            fontWeight: '700',
                            color: facultyVerdict === 'VERIFIED_DISQUALIFIED' ? '#991b1b' : '#334155'
                          }}>
                            <input
                              type="radio"
                              name="facultyVerdict"
                              value="VERIFIED_DISQUALIFIED"
                              checked={facultyVerdict === 'VERIFIED_DISQUALIFIED'}
                              onChange={() => setFacultyVerdict('VERIFIED_DISQUALIFIED')}
                            />
                            <span>Confirm Disqualification</span>
                          </label>

                          <label style={{
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: facultyVerdict === 'RETAKE_GRANTED' ? '2px solid #16a34a' : '1px solid #cbd5e1',
                            background: facultyVerdict === 'RETAKE_GRANTED' ? '#f0fdf4' : '#ffffff',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '12.5px',
                            fontWeight: '700',
                            color: facultyVerdict === 'RETAKE_GRANTED' ? '#15803d' : '#334155'
                          }}>
                            <input
                              type="radio"
                              name="facultyVerdict"
                              value="RETAKE_GRANTED"
                              checked={facultyVerdict === 'RETAKE_GRANTED'}
                              onChange={() => setFacultyVerdict('RETAKE_GRANTED')}
                            />
                            <span>Grant Retake Opportunity</span>
                          </label>
                        </div>

                        <div style={{ marginBottom: '12px' }}>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>
                            Faculty Review Comments
                          </label>
                          <textarea
                            rows={2}
                            value={facultyAuditNotes}
                            onChange={(e) => setFacultyAuditNotes(e.target.value)}
                            placeholder="Enter official justification..."
                            style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '12px', outline: 'none', resize: 'vertical' }}
                          />
                        </div>

                        <button
                          type="button"
                          onClick={handleVerifyVerdict}
                          disabled={isSubmittingVerdict}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            borderRadius: '8px',
                            background: '#0f172a',
                            color: '#ffffff',
                            border: 'none',
                            fontSize: '12.5px',
                            fontWeight: '800',
                            cursor: isSubmittingVerdict ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                        >
                          <CheckCircle2 size={14} />
                          {isSubmittingVerdict ? 'Submitting...' : 'Submit Verification Verdict'}
                        </button>
                      </div>
                    )}

                    {selectedProctorReport.faculty_audit && (
                      <div style={{ background: '#f0fdf4', padding: '10px 14px', borderRadius: '10px', border: '1px solid #bbf7d0', fontSize: '11.5px' }}>
                        <span style={{ color: '#166534', fontWeight: '800', textTransform: 'uppercase', display: 'block', fontSize: '10px' }}>Previous Faculty Audit</span>
                        <div style={{ color: '#14532d', fontWeight: '700' }}>{selectedProctorReport.faculty_audit.verified_by}</div>
                        <div style={{ color: '#166534', fontSize: '11px' }}>Verdict: {selectedProctorReport.faculty_audit.verdict}</div>
                      </div>
                    )}

                  </div>

                </div>

                {/* Studio Bottom Bar */}
                <div style={{ padding: '12px 24px', background: '#ffffff', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                  <div style={{ color: '#64748b', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Shield size={14} color="#6366f1" />
                    <span>EdvergenceX Assessment Integrity Engine • Real-time Multimodal Proctoring</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedProctorReport(null);
                      setIsPlayingSequence(false);
                    }}
                    style={{ padding: '8px 20px', borderRadius: '8px', background: '#0f172a', color: '#ffffff', border: 'none', fontWeight: '700', cursor: 'pointer', fontSize: '13px', transition: 'all 0.2s ease' }}
                  >
                    Close Window
                  </button>
                </div>

              </div>
            </div>
          );
        })()}

        {/* Fullscreen Snapshot Lightbox */}
        {lightboxSnapshot && (
          <div
            onClick={() => setLightboxSnapshot(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
          >
            <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
              <img src={lightboxSnapshot} alt="Enlarged Evidence Snapshot" style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: '12px', border: '2px solid rgba(255,255,255,0.2)' }} />
              <button
                onClick={() => setLightboxSnapshot(null)}
                style={{ position: 'absolute', top: '-16px', right: '-16px', width: '36px', height: '36px', borderRadius: '50%', background: '#dc2626', color: '#fff', border: 'none', fontWeight: '800', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) return <Loader />;


  return (
    <div className="dashboard-wrapper faculty-dashboard">
      {/* AI Disclaimer Modal — shown once per session before any AI feature */}
      {showAiDisclaimer && (
        <AiDisclaimerCard
          featureName={aiDisclaimerFeature}
          onAgree={() => {
            sessionStorage.setItem('ai_disclaimer_agreed', '1');
            setShowAiDisclaimer(false);
            if (pendingAiAction) {
              pendingAiAction();
              setPendingAiAction(null);
            }
          }}
          onCancel={() => {
            setShowAiDisclaimer(false);
            setPendingAiAction(null);
          }}
        />
      )}
      {/* Sleek Pop Notification Toast Banner */}
      {popNotification && (
        <div className={`pop-notification-toast ${popNotification.type || 'success'}`} role="alert">
          {popNotification.type === 'error' ? (
            <AlertCircle size={22} color="#ef4444" style={{ flexShrink: 0, marginTop: '2px' }} />
          ) : (
            <CheckCircle size={22} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '800', fontSize: '15px', color: '#0f172a', marginBottom: '3px' }}>
              {popNotification.title}
            </div>
            <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.45' }}>
              {popNotification.message}
            </div>
          </div>
          <button
            type="button"
            className="pop-notification-close"
            onClick={() => setPopNotification(null)}
            title="Dismiss notification"
          >
            <X size={18} />
          </button>
        </div>
      )}

      <aside className="sidebar">
        <nav className="sidebar-nav">
          <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}>
            <LayoutDashboard size={20} /> Dashboard
          </button>
          <FeatureGate feature="lms">
            <button className={activeTab === 'courses' ? 'active' : ''} onClick={() => setActiveTab('courses')}>
              <BookOpen size={20} /> My Courses
            </button>
          </FeatureGate>
          <FeatureGate feature="lms">
            <button className={activeTab === 'students' ? 'active' : ''} onClick={() => setActiveTab('students')}>
              <GraduationCap size={20} /> Students
            </button>
          </FeatureGate>
          <FeatureGate feature="messages">
            <button className={activeTab === 'chat' ? 'active' : ''} onClick={() => setActiveTab('chat')}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <MessageSquare size={20} /> Messages
                </div>
              {unreadMessagesCount > 0 && (
                <span style={{
                  background: 'var(--danger)',
                  color: 'white',
                  borderRadius: '50%',
                  minWidth: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  padding: '0 6px'
                }}>
                  {unreadMessagesCount}
                </span>
              )}
            </div>
          </button>
           </FeatureGate>
          <FeatureGate feature="discussions">
          <button className={activeTab === 'discussions' ? 'active' : ''} onClick={() => setActiveTab('discussions')}>
            <Users size={20} /> Groups
          </button>
          </FeatureGate>
          <SubFeatureGate feature="proctoring" sub="proctoring_reports_view">
          <button className={activeTab === 'assessmentReports' ? 'active' : ''} onClick={() => setActiveTab('assessmentReports')}>
            <Shield size={20} /> Assessment Reports
          </button>
          </SubFeatureGate>
          <FeatureGate feature="ai_student_reports">
            <button className={activeTab === 'ai_reports' ? 'active' : ''} onClick={() => setActiveTab('ai_reports')}>
              <Brain size={20} /> AI Student Reports
            </button>
          </FeatureGate>
          <button className={activeTab === 'profile' ? 'active' : ''} onClick={() => setActiveTab('profile')}>
            <User size={20} /> Profile
          </button>
        </nav>
      </aside>

      <main className="main-content">
        <header className="top-bar">
          <h1>
            {activeTab === 'dashboard' ? 'Faculty Dashboard' :
              activeTab === 'courses' ? (selectedAssignment ? 'Course Details' : 'Faculty Courses') :
                activeTab === 'students' ? 'Student Directory' :
                  activeTab === 'chat' ? 'Messages' :
                    activeTab === 'discussions' ? 'Groups' :
                      activeTab === 'assessmentReports' ? 'Assessment Proctoring Reports' :
                      activeTab === 'ai_reports' ? 'AI Student Reports' :
                        'Settings'}
          </h1>

          <div className="top-bar-right">
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <button 
                title="Notifications" 
                onClick={() => setShowNotifs(!showNotifs)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--faculty-muted)', position: 'relative', marginRight: '16px', zIndex: 10 }}
              >
                <Bell size={22} />
                {notifications.some(n => !n.is_read) && (
                  <span style={{
                    position: 'absolute', top: '-2px', right: '-2px',
                    width: '10px', height: '10px', background: '#ef4444',
                    borderRadius: '50%', border: '2px solid white'
                  }}></span>
                )}
              </button>
              
              {showNotifs && (
                <div className="uni-card" style={{
                  position: 'absolute', top: '40px', right: '10px', width: '320px',
                  zIndex: 100, padding: '16px', border: '1px solid var(--border)',
                  boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', background: 'var(--bg-card)', borderRadius: '12px', textAlign: 'left'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>Notifications</h4>
                    <button onClick={() => setShowNotifs(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                      <X size={16} color="var(--text-muted)" />
                    </button>
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {notifications.length > 0 ? notifications.map(n => (
                      <div key={n.id} style={{ padding: '10px', borderBottom: '1px solid var(--border)', fontSize: '13px' }}>
                        <div style={{ fontWeight: 600 }}>{n.title}</div>
                        <div style={{ color: 'var(--text-muted)', marginTop: '2px' }}>{n.message}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>{new Date(n.created_at).toLocaleString()}</div>
                      </div>
                    )) : <p style={{ fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center', padding: '10px' }}>No new notifications</p>}
                  </div>
                </div>
              )}
            </div>

            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <button 
                className={`faculty-btn-secondary ${showCalendarModal ? 'active' : ''}`}
                onClick={() => setShowCalendarModal(!showCalendarModal)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', zIndex: 10 }}
                title="Calendar View"
              >
                <Calendar size={16} />
              </button>
              
              {showCalendarModal && (
                <div className="uni-card" style={{
                  position: 'absolute', top: '40px', right: 0,
                  zIndex: 100, padding: '16px', border: '1px solid var(--border)',
                  boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', background: 'var(--bg-card)', borderRadius: '12px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>Calendar</h4>
                    <button onClick={() => setShowCalendarModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                      <X size={16} color="var(--text-muted)" />
                    </button>
                  </div>
                  <CalendarLib
                    onChange={setSelectedDate}
                    value={selectedDate}
                  />
                </div>
              )}
            </div>
            
          </div>
        </header>

        <section className="dashboard-content faculty-dashboard-content">
          <div className="unidash-fade-in">
            {activeTab === 'dashboard' && (
              <div>
                {coursesLoading || studentsLoading || isAnalyticsLoading ? (
                  <div className="faculty-card" style={{ padding: '32px', textAlign: 'center', color: 'var(--faculty-muted)' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', fontWeight: 600 }}>
                      <div style={{ width: '16px', height: '16px', border: '2px solid rgba(5,168,243,0.2)', borderTopColor: '#05a8f3', borderRadius: '50%', animation: 'spin 0.9s linear infinite' }} />
                      Loading course analytics...
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: '28px' }}>
                      <h2 style={{ fontSize: '2rem', color: 'var(--faculty-text)', margin: 0, fontWeight: '700' }}>Welcome back, {profile?.faculty_profile?.full_name || 'Faculty'}!</h2>
                      <p style={{ color: 'var(--faculty-muted)', fontSize: '1.05rem', marginTop: '10px' }}>Here's a clean overview of the classes, batches, and academic performance you are monitoring.</p>
                    </div>

                    <div className="faculty-analytics-grid">
                      <div className="faculty-analytics-card faculty-card-blue">
                        <div className="faculty-analytics-icon"><BookOpen size={20} /></div>
                        <div>
                          <div className="faculty-analytics-label">Courses</div>
                          <div className="faculty-analytics-value">{facultyAnalyticsSummary.totalCourses}</div>
                        </div>
                      </div>

                      <div className="faculty-analytics-card faculty-card-green">
                        <div className="faculty-analytics-icon"><Users size={20} /></div>
                        <div>
                          <div className="faculty-analytics-label">Students</div>
                          <div className="faculty-analytics-value">{facultyAnalyticsSummary.totalStudents}</div>
                        </div>
                      </div>

                      <div className="faculty-analytics-card faculty-card-purple">
                        <div className="faculty-analytics-icon"><Layers size={20} /></div>
                        <div>
                          <div className="faculty-analytics-label">Batches</div>
                          <div className="faculty-analytics-value">{facultyAnalyticsSummary.uniqueBatches}</div>
                        </div>
                      </div>

                      <div className="faculty-analytics-card faculty-card-orange">
                        <div className="faculty-analytics-icon"><Clock size={20} /></div>
                        <div>
                          <div className="faculty-analytics-label">Avg Attendance</div>
                          <div className="faculty-analytics-value">{facultyAnalyticsSummary.avgAttendance}%</div>
                        </div>
                      </div>
                    </div>

                    {/* Temporarily hidden until these dashboard summaries are needed again.
                    <div className="faculty-chart-grid">
                      <div className="faculty-chart-card">
                        <div className="faculty-chart-header">
                          <h3>Student Performance Distribution</h3>
                        </div>
                        <div className="faculty-chart-box">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={performanceDistribution}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="range" tick={{ fontSize: 11, fill: '#475569' }} />
                              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#475569' }} />
                              <Tooltip formatter={(value) => [`${value} students`, 'Students']} />
                              <Bar dataKey="students" name="Students" radius={[8, 8, 0, 0]}>
                                {performanceDistribution.map((entry) => (
                                  <Cell key={entry.range} fill={entry.fill} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      <div className="faculty-chart-card">
                        <div className="faculty-chart-header">
                          <h3>Attendance Overview</h3>
                          <select
                            className="faculty-attendance-date-filter"
                            value={selectedAttendanceDate}
                            onChange={(event) => setSelectedAttendanceDate(event.target.value)}
                            aria-label="Filter attendance by date"
                          >
                            <option value="all">All dates</option>
                            {attendanceDistribution.slice().reverse().map((item) => (
                              <option key={item.date} value={item.date}>{item.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="faculty-chart-box">
                          {visibleAttendanceDistribution.length === 0 ? (
                            <div className="faculty-attendance-empty">No attendance dates have been recorded yet.</div>
                          ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={visibleAttendanceDistribution} barGap={6}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#475569' }} />
                              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#475569' }} />
                              <Tooltip formatter={(value, name) => [`${value} students`, name === 'attendedStudents' ? 'Attended' : 'Total students']} />
                              <Legend formatter={(value) => value === 'attendedStudents' ? 'Attended' : 'Total students'} />
                              <Bar dataKey="totalStudents" name="totalStudents" fill="#cbd5e1" radius={[7, 7, 0, 0]} />
                              <Bar dataKey="attendedStudents" name="attendedStudents" fill="#22c55e" radius={[7, 7, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                          )}
                        </div>
                      </div>
                    </div>
                    */}

                    <FacultySkillGapAnalysis
                      students={facultyStudents}
                      allowedBatches={assignedCourses
                        .map((assignment) => assignment.batch || assignment.academic_batch)
                        .filter(Boolean)}
                    />

                    <div className="uni-card" style={{ padding: '24px', position: 'relative', marginTop: '24px' }}>
                      <h3 style={{ color: 'var(--faculty-text)', margin: '0 0 18px 0', fontSize: '20px', fontWeight: '800' }}>
                        Recent Course Assignments
                      </h3>
                      <CardInfoTooltip title="Course Assignments" content="Recently assigned courses and their completion status." />
                      {coursesLoading ? (
                        <p style={{ color: 'var(--faculty-muted)', margin: 0 }}>Loading assigned courses...</p>
                      ) : assignedCourses.length === 0 ? (
                        <div className="empty-state" style={{ textAlign: 'center', padding: '50px' }}>
                          <LayoutDashboard size={42} style={{ color: 'var(--faculty-border)', marginBottom: '14px' }} />
                          <h3 style={{ color: 'var(--faculty-muted)', marginBottom: '8px', fontSize: '18px' }}>No assignments yet</h3>
                          <p style={{ color: 'var(--faculty-faint)' }}>Courses assigned by your university administrator will appear here.</p>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                          {assignedCourses.slice(0, 3).map(item => (
                            <div
                              key={item.id}
                              onClick={() => { setSelectedAssignment(item); setCourseDetailSection('attendance'); setActiveTab('courses'); }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  setSelectedAssignment(item);
                                  setCourseDetailSection('attendance');
                                  setActiveTab('courses');
                                }
                              }}
                              style={{ background: 'var(--faculty-panel)', border: '1px solid var(--faculty-border)', borderRadius: '12px', padding: '18px', cursor: 'pointer', boxShadow: '0 4px 14px rgba(15,40,65,0.04)', transition: 'transform 0.18s ease, border-color 0.18s ease' }}
                            >
                              <span className="status-tag">{item.batch || 'All Batches'}</span>
                              <h4 style={{ color: 'var(--faculty-text)', fontSize: '17px', margin: '14px 0 8px 0', lineHeight: 1.35, fontWeight: '800' }}>{item.course?.title || 'Assigned Course'}</h4>
                              <p style={{ color: 'var(--faculty-muted)', margin: 0, fontSize: '14px', lineHeight: 1.5 }}>{item.course?.category || 'Curriculum'}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'courses' && (
              <div>
                {!selectedAssignment && (
                  <div className="faculty-page-heading">
                    <h2>My Courses</h2>
                    <p>Manage your courses and view student progress.</p>
                  </div>
                )}

                {selectedAssignment ? renderCourseDetail() : coursesLoading ? (
                  <div className="uni-card" style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
                    Loading assigned courses...
                  </div>
                ) : assignedCourses.length === 0 ? (
                  <div className="uni-card empty-state" style={{ textAlign: 'center', padding: '60px' }}>
                    <BookOpen size={48} style={{ color: 'var(--border)', marginBottom: '16px' }} />
                    <h3 style={{ color: 'var(--text-muted)', marginBottom: '10px', fontSize: '20px' }}>No courses assigned yet</h3>
                    <p style={{ color: 'var(--text-faint)' }}>Your university administrator will assign you to courses and batches.</p>
                  </div>
                ) : (
                  <div className="faculty-course-grid">
                    {assignedCourses.map(item => {
                      const course = item.course || {};
                      const moduleCount = Array.isArray(course.modules_data) ? course.modules_data.length : 0;
                      const lessonCount = getLessonCount(course);
                      return (
                        <div
                          key={item.id}
                          className="faculty-course-card"
                          onClick={() => { setSelectedAssignment(item); setCourseDetailSection('attendance'); }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              setSelectedAssignment(item);
                              setCourseDetailSection('attendance');
                            }
                          }}
                        >
                          <div className="faculty-card-main">
                            <div className="faculty-card-kicker">
                              <span>{course.category || 'Curriculum'}</span>
                              <span>{item.batch || 'All Batches'}</span>
                            </div>
                            <h3>{course.title || 'Assigned Course'}</h3>
                            <div style={{ flex: 1, marginBottom: '16px' }}>
                              <p className="course-card-description" style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>
                                {course.description || 'Course details will appear here once the curriculum is available.'}
                              </p>
                              {course.description && course.description.length > 150 && (
                                <button 
                                  onClick={(e) => { e.stopPropagation(); setFullDescCourse(course); }}
                                  style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: 0, fontSize: '0.85rem', fontWeight: 600, marginTop: '4px' }}
                                >
                                  Read More
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="faculty-card-action">
                            <span>View Course & Batch Roster</span>
                            <ArrowRight size={18} />
                          </div>
                        </div>


                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {activeTab === 'students' && <FacultyStudentDirectory />}

            {activeTab === 'chat' && <FeatureGate feature="messages"><ChatPage embedded={true} /></FeatureGate>}

            {activeTab === 'discussions' && <FeatureGate feature="discussions" fallback={<FeatureUnavailable featureLabel="Groups & Discussions" />}><DiscussionsPage embedded={true} /></FeatureGate>}
            {activeTab === 'assessmentReports' && renderAssessmentReportsTab()}
            {activeTab === 'ai_reports' && (
              <FeatureGate
                feature="ai_student_reports"
                fallback={<div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>AI Student Reports feature is not enabled for your institution.</div>}
              >
                <UniAiReports showToast={(msg, type) => triggerNotification(type || 'success', 'AI Student Reports', msg)} />
              </FeatureGate>
            )}
            {activeTab === 'profile' && (

              <div className="faculty-profile-page">
                <div className="chart-header" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Settings size={18} style={{ color: 'var(--text-faint)' }} />
                    <span style={{ color: 'var(--text-faint)', fontSize: '15px', fontWeight: '500' }}>Faculty Profile Settings</span>
                  </div>
                  {!isEditing ? (
                    <button
                      className="uni-btn-primary"
                      onClick={() => setIsEditing(true)}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px' }}
                    >
                      <Edit2 size={16} />
                      Edit Profile
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button className="uni-btn-secondary" style={{ padding: '10px 20px', borderRadius: '8px' }} onClick={() => setIsEditing(false)}>Cancel</button>
                      <button className="uni-btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', opacity: isFormValid ? 1 : 0.5, cursor: isFormValid ? 'pointer' : 'not-allowed' }} onClick={handleSaveProfile} disabled={!isFormValid}>
                        <Save size={16} /> Save Changes
                      </button>
                    </div>
                  )}
                </div>

                {/* Settings Tabs - Premium Design */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  marginBottom: '24px',
                  borderBottom: '1px solid var(--border)',
                  paddingBottom: '0px'
                }}>
                  <button
                    style={{
                      padding: '12px 20px',
                      background: 'none',
                      border: 'none',
                      borderBottom: '2px solid #3b82f6',
                      color: '#3b82f6',
                      fontWeight: 600,
                      fontSize: '15px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <User size={18} />
                    Faculty Profile
                  </button>
                </div>

                <div className="uni-card faculty-profile-card" style={{ maxWidth: '1200px' }}>
                  {/* Header Section */}
                  <div className="faculty-profile-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px', borderBottom: '2px solid var(--border)', paddingBottom: '20px' }}>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                      {/* Avatar */}
                      <div style={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '36px',
                        fontWeight: '700',
                        color: 'white',
                        border: '3px solid var(--border)'
                      }}>
                        {profile?.faculty_profile?.full_name?.charAt(0) || 'F'}
                      </div>

                      <div>
                        <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--faculty-text)', marginBottom: '6px' }}>
                          {profile?.faculty_profile?.full_name || 'Faculty Member'}
                        </h2>
                        <p style={{ color: 'var(--faculty-muted)', fontSize: '15px', marginBottom: '8px' }}>{profile?.faculty_profile?.designation || 'N/A'}</p>
                        <div className="uni-profile-badge" style={{
                          background: 'var(--faculty-accent-soft)',
                          color: 'var(--faculty-accent)',
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <CheckCircle size={14} />
                          Verified Faculty
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Settings Sections */}
                  <div style={{ display: 'grid', gap: '30px' }}>
                    {/* BASIC INFORMATION */}
                    <div className="settings-section">
                      <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--faculty-text)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Building size={20} style={{ color: '#3b82f6' }} />
                        Basic Information
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

                        <div className="uni-setting-group">
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--faculty-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>Full Name *</label>
                          {isEditing ? (
                            <div>
                              <input
                                type="text"
                                value={formData.full_name}
                                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                                style={{ width: '100%', padding: '10px 12px', border: `2px solid ${formErrors.full_name ? '#ef4444' : 'var(--faculty-border)'}`, borderRadius: '8px', fontSize: '15px', fontWeight: '500', outline: 'none' }}
                              />
                              {formErrors.full_name && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', fontWeight: '500' }}>{formErrors.full_name}</div>}
                            </div>
                          ) : (
                            <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--faculty-text)' }}>{profile?.faculty_profile?.full_name || 'N/A'}</div>
                          )}
                        </div>

                        <div className="uni-setting-group">
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--faculty-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>Email Address (Read-only)</label>
                          <div style={{ width: '100%', padding: isEditing ? '10px 12px' : '0', background: isEditing ? '#f8fafc' : 'transparent', border: isEditing ? '2px solid #f1f5f9' : '2px solid transparent', borderRadius: '8px', fontSize: '15px', fontWeight: '500', color: 'var(--faculty-muted)' }}>{profile?.email || 'N/A'}</div>
                        </div>

                        <div className="uni-setting-group">
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--faculty-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>Phone Number *</label>
                          {isEditing ? (
                            <div>
                              <input
                                type="text"
                                value={formData.phone_number}
                                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                                style={{ width: '100%', padding: '10px 12px', border: `2px solid ${formErrors.phone_number ? '#ef4444' : 'var(--faculty-border)'}`, borderRadius: '8px', fontSize: '15px', fontWeight: '500', outline: 'none' }}
                              />
                              {formErrors.phone_number && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', fontWeight: '500' }}>{formErrors.phone_number}</div>}
                            </div>
                          ) : (
                            <div style={{ fontSize: '15px', fontWeight: '500', color: 'var(--faculty-text)' }}>{profile?.phone_number || 'N/A'}</div>
                          )}
                        </div>

                        <div className="uni-setting-group">
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--faculty-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>Faculty ID *</label>
                          <div style={{ width: '100%', padding: isEditing ? '10px 12px' : '0', background: isEditing ? '#f8fafc' : 'transparent', border: isEditing ? '2px solid #f1f5f9' : '2px solid transparent', borderRadius: '8px', fontSize: '15px', fontWeight: '500', color: 'var(--faculty-muted)' }}>{profile?.faculty_profile?.faculty_id || 'N/A'}</div>
                        </div>

                        <div className="uni-setting-group">
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--faculty-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>Department *</label>
                          <div style={{ width: '100%', padding: isEditing ? '10px 12px' : '0', background: isEditing ? '#f8fafc' : 'transparent', border: isEditing ? '2px solid #f1f5f9' : '2px solid transparent', borderRadius: '8px', fontSize: '15px', fontWeight: '500', color: 'var(--faculty-muted)' }}>{profile?.faculty_profile?.department || 'N/A'}</div>
                        </div>

                        <div className="uni-setting-group">
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--faculty-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>Designation *</label>
                          <div style={{ width: '100%', padding: isEditing ? '10px 12px' : '0', background: isEditing ? '#f8fafc' : 'transparent', border: isEditing ? '2px solid #f1f5f9' : '2px solid transparent', borderRadius: '8px', fontSize: '15px', fontWeight: '500', color: 'var(--faculty-muted)' }}>{profile?.faculty_profile?.designation || 'N/A'}</div>
                        </div>

                        <div className="uni-setting-group" style={{ gridColumn: '1 / -1' }}>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--faculty-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>Institution (Managed by University)</label>
                          <div style={{ width: '100%', padding: isEditing ? '10px 12px' : '0', background: isEditing ? '#f8fafc' : 'transparent', border: isEditing ? '2px solid #f1f5f9' : '2px solid transparent', borderRadius: '8px', fontSize: '15px', fontWeight: '500', color: 'var(--faculty-muted)' }}>{profile?.faculty_profile?.institution_name || profile?.faculty_profile?.university_name || 'N/A'}</div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Full Description Modal */}
        {fullDescCourse && (
          <div className="modal-overlay" style={{ zIndex: 999999 }}>
            <div className="modal-box" style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h3>{fullDescCourse.title}</h3>
                <button className="modal-close" onClick={() => setFullDescCourse(null)}>×</button>
              </div>
              <div className="modal-content custom-scrollbar" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <p style={{ color: 'var(--text-main)', fontSize: '0.95rem', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                  {fullDescCourse.description}
                </p>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default FacultyDashboard;
